import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuList,
  Snackbar,
  Alert,
  InputLabel,
  Select,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Business,
  Group,
  CalendarToday,
  Person,
  Edit,
  Palette,
  MoreVert,
  Info,
  AttachMoney,
  Assessment,
  ColorLens,
  CheckCircle,
  RadioButtonUnchecked,
  CleaningServices,
  Login,
  Logout,
  Key,
  ConfirmationNumber,
  Share,
  Language,
  Phone,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  UnfoldLess,
  Receipt,
  AccessTime,
  FilterList,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, addMonths, subMonths, addDays } from 'date-fns';
import { Booking, Property, Unit, BookingHistory, bookingApi, propertyColorApi, documentsApi, userPreferencesApi, dailyRatesApi } from '../services/api';
import { AccountingService } from '../services/accountingService';
import { getApiUrl } from '../config/api';
import ExpenseDialog from './ExpenseDialog';
import PurchaseInvoiceDialog from './PurchaseInvoiceDialog';
import { is, tr } from 'date-fns/locale';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { sanitizeHtml } from '../utils/sanitize';

interface BookingCalendarProps {
  bookings: Booking[];
  properties: Property[];
  units: Unit[];
  onCreateBooking?: () => void;
  onEditBooking?: (booking: Booking) => void;
  onViewBooking?: (booking: Booking) => void;
  onDayCellClick?: (date: Date, propertyId: number, unitId: number) => void;
  onRefresh?: () => void;
  showSectionsAboveCalendar?: boolean;
  sidebarCollapsed?: boolean;
  drawerWidth?: number;
}

interface CalendarBooking extends Booking {
  color: string;
  icon: React.ReactNode;
  displayName: string;
  propertyName: string;
  unitName: string;
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  bookings,
  properties,
  units,
  onCreateBooking,
  onEditBooking,
  onViewBooking,
  onDayCellClick,
  onRefresh,
  showSectionsAboveCalendar = true,
  sidebarCollapsed = false,
  drawerWidth = 240,
}) => {
  // Refs for synchronized scrolling
  const unitsScrollRef = React.useRef<HTMLDivElement>(null);
  const calendarScrollRef = React.useRef<HTMLDivElement>(null);
  const dayHeaderScrollRef = React.useRef<HTMLDivElement>(null);
  
  // State for showing/hiding day numbers
  const [showDayNumbers, setShowDayNumbers] = React.useState(() => {
    const savedMode = localStorage.getItem('calendar_day_display_mode');
    return savedMode !== 'none';
  });
  
  // Day display mode state
  const [dayDisplayMode, setDayDisplayMode] = React.useState<'none' | 'day_numbers' | 'rates'>(() => {
    return (localStorage.getItem('calendar_day_display_mode') as 'none' | 'day_numbers' | 'rates') || 'day_numbers';
  });
  
  // Calendar mode state
  const [calendarMode, setCalendarMode] = React.useState<'booking' | 'change_rate'>(() => {
    return (localStorage.getItem('calendar_mode') as 'booking' | 'change_rate') || 'booking';
  });
  
  // Change rate state
  const [selectedRateCell, setSelectedRateCell] = React.useState<{
    date: Date;
    unitId: number;
    propertyId: number;
  } | null>(null);
  const [editingRate, setEditingRate] = useState<string>('');
  const [selectedCells, setSelectedCells] = useState<Array<{
    date: Date;
    unitId: number;
    propertyId: number;
  }>>([]);
  const [isSelectingCells, setIsSelectingCells] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{
    date: Date;
    unitId: number;
    propertyId: number;
  } | null>(null);
  const [hasDragged, setHasDragged] = useState(false);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState<CalendarBooking | null>(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [ticketLanguage, setTicketLanguage] = useState<'ar' | 'en'>('ar');
  const [ticketDocuments, setTicketDocuments] = useState<any[]>([]);
  const [ticketDocumentContent, setTicketDocumentContent] = useState<string>('');
  const [colorUpdateTrigger, setColorUpdateTrigger] = useState(0); // Force re-render when colors change
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedPropertyForColor, setSelectedPropertyForColor] = useState<number | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  const [propertyMenuAnchor, setPropertyMenuAnchor] = useState<HTMLElement | null>(null);
  const [unitMenuAnchor, setUnitMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedPropertyForMenu, setSelectedPropertyForMenu] = useState<Property | null>(null);
  const [selectedUnitForMenu, setSelectedUnitForMenu] = useState<Unit | null>(null);
  const [showColorEditDialog, setShowColorEditDialog] = useState(false);
  const [tempBackgroundColor, setTempBackgroundColor] = useState('');
  const [tempTextColor, setTempTextColor] = useState('');
  const [bookingHistory, setBookingHistory] = useState<BookingHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [editPaymentAmount, setEditPaymentAmount] = useState('');
  const [editPaymentReason, setEditPaymentReason] = useState('');
  const [editingPaymentHistory, setEditingPaymentHistory] = useState<BookingHistory | null>(null);
  const [editPaymentLoading, setEditPaymentLoading] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseProperty, setExpenseProperty] = useState<Property | null>(null);
  const [expenseUnit, setExpenseUnit] = useState<Unit | null>(null);
  
  // Purchase Invoice dialog states
  const [showPurchaseInvoiceDialog, setShowPurchaseInvoiceDialog] = useState(false);
  const [purchaseInvoiceProperty, setPurchaseInvoiceProperty] = useState<Property | null>(null);
  const [purchaseInvoiceUnit, setPurchaseInvoiceUnit] = useState<Unit | null>(null);
  
  // Check-in/out dialog states
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [selectedBookingForCheckin, setSelectedBookingForCheckin] = useState<Booking | null>(null);
  const [checkinStatus, setCheckinStatus] = useState<'not_checked_in' | 'checked_in' | 'checked_out'>('not_checked_in');
  const [checkinLoading, setCheckinLoading] = useState(false);

  // Cleaning dialog state
  const [showCleaningDialog, setShowCleaningDialog] = useState(false);
  const [selectedUnitForCleaning, setSelectedUnitForCleaning] = useState<Unit | null>(null);
  const [cleaningStatus, setCleaningStatus] = useState<'cleaned' | 'not_cleaned'>('cleaned');
  const [cleaningLoading, setCleaningLoading] = useState(false);

  // Filter state
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'reserved' | 'non_reserved'>('all');
  const [filterStartDate, setFilterStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  

  const [filterEndDate, setFilterEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Persist Calendar Mode and Day Display
  React.useEffect(() => {
    localStorage.setItem('calendar_day_display_mode', dayDisplayMode);
  }, [dayDisplayMode]);

  React.useEffect(() => {
    localStorage.setItem('calendar_mode', calendarMode);
  }, [calendarMode]);

  // State for hovered cell information
  const [hoveredCellInfo, setHoveredCellInfo] = useState<{
    date: Date | null;
    unitId: number | null;
    propertyId: number | null;
  }>({ date: null, unitId: null, propertyId: null });

  // Drag and drop state
  const [draggedBooking, setDraggedBooking] = useState<CalendarBooking | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ date: Date; unitId: number; propertyId: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragFeedback, setDragFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  
  // Expandable hierarchy state
  const [expandedUnits, setExpandedUnits] = useState<Set<number>>(() => {
    // Load expanded state from localStorage
    const saved = localStorage.getItem('calendar-expanded-units');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return new Set(parsed);
      } catch (error) {
        console.error('Failed to parse saved expanded units:', error);
      }
    }
    return new Set();
  });

  // Units column width state
  const [unitsColumnWidth, setUnitsColumnWidth] = useState<number>(() => {
    // Load saved width from localStorage
    const saved = localStorage.getItem('calendar-units-column-width');
    return saved ? parseInt(saved) : 200;
  });

  // Resize handle state
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('calendar-expanded-units', JSON.stringify(Array.from(expandedUnits)));
  }, [expandedUnits]);

  // Save units column width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('calendar-units-column-width', unitsColumnWidth.toString());
  }, [unitsColumnWidth]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(unitsColumnWidth);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const deltaX = e.clientX - startX;
    const newWidth = Math.max(150, Math.min(500, startWidth + deltaX)); // Min 150px, max 500px
    setUnitsColumnWidth(newWidth);
  }, [isResizing, startX, startWidth]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Add global mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Initialize expanded units (expand root units by default)
  useEffect(() => {
    if (units.length > 0) {
      const rootUnitsWithChildren = units.filter(unit => 
        unit.level === 0 && units.some(u => u.parent_unit_id === unit.id)
      );
      
      // Check if we have any saved state
      const savedExpanded = localStorage.getItem('calendar-expanded-units');
      
      if (!savedExpanded && rootUnitsWithChildren.length > 0) {
        // No saved state - expand all root units by default
        console.log('No saved state, expanding root units by default:', rootUnitsWithChildren.map(u => `${u.unit_number} (${u.id})`));
        setExpandedUnits(new Set(rootUnitsWithChildren.map(unit => unit.id)));
      }
    }
  }, [units]); // Removed expandedUnits.size dependency to avoid infinite loop
  

  // Load property colors from backend
  const { data: propertyColors = [], isLoading: isLoadingColors } = useQuery({
    queryKey: ['propertyColors'],
    queryFn: propertyColorApi.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Debug logging for property colors
  React.useEffect(() => {
    console.log('Property colors loaded:', propertyColors);
    if (isLoadingColors) {
      console.log('Loading property colors...');
    }
  }, [propertyColors, isLoadingColors]);

  // Fetch currencies for currency display
  const { data: currencies = [], isLoading: isLoadingCurrencies, error: currenciesError } = useQuery({
    queryKey: ['currencies'],
    queryFn: async () => {
      console.log('Fetching currencies...');
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      // Ensure we always return an array
      const currenciesArray = Array.isArray(data) ? data : (data.data || []);
      console.log('Currencies fetched:', currenciesArray);
      return currenciesArray;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Debug logging for currencies
  React.useEffect(() => {
    if (currenciesError) {
      console.error('Failed to load currencies:', currenciesError);
    }
  }, [currenciesError]);

  // Mutation for updating booking check-in status
  const updateBookingCheckinMutation = useMutation({
    mutationFn: ({ bookingId, checkinData }: { 
      bookingId: number; 
      checkinData: { 
        checkin_status: string;
        checkin_time?: string | null;
        checkout_time?: string | null;
      }
    }) => {
      return fetch(getApiUrl(`/bookings/${bookingId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkinData),
      }).then(response => {
        if (!response.ok) {
          throw new Error('Failed to update booking check-in status');
        }
        return response.json();
      });
    },
    onSuccess: (data) => {
      console.log('Booking check-in status updated successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
      if (onRefresh) onRefresh();
    },
    onError: (error) => {
      console.error('Failed to update booking check-in status:', error);
    },
  });

  // Mutation for updating unit cleaning status
  const updateUnitCleaningMutation = useMutation({
    mutationFn: async ({ unitId, cleaningStatus }: { unitId: number; cleaningStatus: string }) => {
      const response = await fetch(getApiUrl(`/units/${unitId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-HTTP-Method-Override': 'PUT',
        },
        body: JSON.stringify({ cleaning_status: cleaningStatus }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`Failed to update unit cleaning status: ${response.status}`);
      }
      
      const result = await response.json();
      return result;
    },
    onSuccess: (data, variables) => {
      console.log('Unit cleaning status updated successfully:', data, variables);
      // Directly update the units cache with the new cleaning status
      queryClient.setQueryData(['units'], (oldUnits: any[]) => {
        if (!oldUnits) return oldUnits;
        
        return oldUnits.map(unit => {
          if (unit.id === variables.unitId) {
            return {
              ...unit,
              cleaning_status: variables.cleaningStatus
            };
          }
          return unit;
        });
      });
      
      // IMPORTANT: Also update units-all cache (used by BookingsPage)
      queryClient.setQueryData(['units-all'], (oldUnits: any[]) => {
        if (!oldUnits) return oldUnits;
        
        return oldUnits.map(unit => {
          if (unit.id === variables.unitId) {
            return {
              ...unit,
              cleaning_status: variables.cleaningStatus
            };
          }
          return unit;
        });
      });
      
      // Also update bookings cache if needed (for any related bookings)
      queryClient.setQueryData(['bookings'], (oldBookings: any[]) => {
        if (!oldBookings) return oldBookings;
        
        return oldBookings.map(booking => {
          if (booking.unit_id === variables.unitId) {
            return {
              ...booking,
              unit: booking.unit ? {
                ...booking.unit,
                cleaning_status: variables.cleaningStatus
              } : booking.unit
            };
          }
          return booking;
        });
      });
      
      // Still invalidate queries as backup to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['units-all'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      
      if (onRefresh) onRefresh();
    },
    onError: (error) => {
      console.error('Unit cleaning status update failed:', error);
    },
  });

  // Helper function to get main currency symbol (default currency)
  const getMainCurrencySymbol = () => {
    if (!Array.isArray(currencies) || currencies.length === 0) {
      return '$';
    }
    const defaultCurrency = currencies.find((c: any) => c.is_default);
    return defaultCurrency?.symbol || '$';
  };

  // Helper function to format amount with currency symbol
  const formatAmountWithCurrency = (booking: any, amountType: string) => {
    if (!booking.display_amounts) {
      // Fallback for old bookings without display_amounts
      const amount = booking[amountType] || 0;
      
      // Currency hierarchy: booking.currency > property.currency > default
      let symbol = '$'; // Default fallback
      
      if (booking.currency?.symbol) {
        symbol = booking.currency.symbol;
      } else if (booking.property?.currency?.symbol) {
        symbol = booking.property.currency.symbol;
      } else {
        symbol = getMainCurrencySymbol();
      }
      
      return `${symbol}${Number(amount).toLocaleString()}`;
    }
    
    const displayAmount = booking.display_amounts[amountType];
    if (!displayAmount) return `${getMainCurrencySymbol()}0`;
    
    const symbol = displayAmount.symbol || getMainCurrencySymbol();
    const value = displayAmount.value || 0;
    return `${symbol}${Number(value).toLocaleString()}`;
  };

  // Helper function to format remaining amount
  const formatRemainingAmount = (booking: any) => {
    if (!booking.display_amounts) {
      // Fallback for old bookings
      const total = Number(booking.total_amount || 0);
      const paid = Number(booking.paid_amount || 0);
      const remaining = Math.max(0, total - paid);
      
      // Currency hierarchy: booking.currency > property.currency > default
      let symbol = '$'; // Default fallback
      
      if (booking.currency?.symbol) {
        symbol = booking.currency.symbol;
      } else if (booking.property?.currency?.symbol) {
        symbol = booking.property.currency.symbol;
      } else {
        symbol = getMainCurrencySymbol();
      }
      
      return `${symbol}${remaining.toLocaleString()}`;
    }
    
    const totalDisplay = booking.display_amounts.total_amount;
    const paidDisplay = booking.display_amounts.paid_amount;
    
    if (!totalDisplay || !paidDisplay) return `${getMainCurrencySymbol()}0`;
    
    const totalValue = Number(totalDisplay.value || 0);
    const paidValue = Number(paidDisplay.value || 0);
    
    // For remaining amount, use the same currency as total amount
    const remaining = Math.max(0, totalValue - paidValue);
    const symbol = totalDisplay.symbol || getMainCurrencySymbol();
    return `${symbol}${remaining.toLocaleString()}`;
  };

  // Create lookup objects for quick access
  const customPropertyColors = useMemo(() => {
    const lookup: { [propertyId: number]: string } = {};
    propertyColors.forEach(color => {
      if (color.background_color) {
        lookup[color.property_id] = color.background_color;
      }
    });
    return lookup;
  }, [propertyColors]);

  const customPropertyTextColors = useMemo(() => {
    const lookup: { [propertyId: number]: string } = {};
    propertyColors.forEach(color => {
      if (color.text_color) {
        lookup[color.property_id] = color.text_color;
      }
    });
    return lookup;
  }, [propertyColors]);

  // Mutation for updating property colors
  const updateColorMutation = useMutation({
    mutationFn: ({ propertyId, background_color, text_color }: { 
      propertyId: number; 
      background_color?: string | null; 
      text_color?: string | null; 
    }) => {
      return propertyColorApi.update(propertyId, { background_color, text_color });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyColors'] });
      setColorUpdateTrigger(prev => prev + 1);
    },
    onError: (error) => {
      console.error('Failed to update property colors:', error);
    },
  });

  // Mutation for updating booking via drag and drop
  const updateBookingMutation = useMutation({
    mutationFn: async ({ bookingId, updateData, originalBooking, newPropertyId }: { 
      bookingId: number; 
      updateData: { 
        start_date?: string;
        end_date?: string;
        unit_id?: number;
        property_id?: number;
      };
      originalBooking?: CalendarBooking;
      newPropertyId?: number;
    }) => {
      console.log('Updating booking:', bookingId, updateData);
      const response = await bookingApi.update(bookingId, updateData);
      return { response, originalBooking, newPropertyId };
    },
    onSuccess: (data) => {
      console.log('Booking updated successfully via drag and drop:', data.response);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
      if (onRefresh) onRefresh();
      
      // Check if currency changed
      let message = 'Booking moved successfully! Booking and transactions updated.';
      let alertType: 'success' | 'info' = 'success';
      
      if (data.originalBooking && data.newPropertyId && data.originalBooking.property_id !== data.newPropertyId) {
        // Find the original and new properties to check their currencies
        const originalProperty = properties.find(p => p.id === data.originalBooking!.property_id);
        const newProperty = properties.find(p => p.id === data.newPropertyId);
        
        if (originalProperty && newProperty && originalProperty.currency_id !== newProperty.currency_id) {
          // Currency changed - show strong alert notification
          const originalCurrency = currencies.find((c: any) => c.id === originalProperty.currency_id);
          const newCurrency = currencies.find((c: any) => c.id === newProperty.currency_id);
          
          const originalSymbol = originalCurrency?.symbol || '$';
          const newSymbol = newCurrency?.symbol || '$';
          const originalName = originalCurrency?.name || 'USD';
          const newName = newCurrency?.name || 'USD';
          
          const currencyAlertMessage = isRTL 
            ? `⚠️ تم تغيير العملة!\n\nتم نقل الحجز بنجاح من ${originalProperty.name} إلى ${newProperty.name}\n\nتم تغيير العملة من: ${originalName} (${originalSymbol})\nإلى: ${newName} (${newSymbol})\n\n⚠️ تحذير: قد تحتاج إلى تحديث المبالغ وفقاً لسعر الصرف الحالي`
            : `⚠️ Currency Changed!\n\nBooking successfully moved from ${originalProperty.name} to ${newProperty.name}\n\nCurrency changed from: ${originalName} (${originalSymbol})\nTo: ${newName} (${newSymbol})\n\n⚠️ Warning: You may need to update amounts according to current exchange rates`;
          
          // Show strong alert notification
          alert(currencyAlertMessage);
          
          // Also show success message in snackbar
          message = isRTL 
            ? 'تم نقل الحجز بنجاح مع تحديث المعاملات المالية'
            : 'Booking moved successfully with financial transactions updated';
          alertType = 'success';
        }
      }
      
      setDragFeedback({ 
        type: alertType, 
        message 
      });
    },
    onError: (error: any) => {
      console.error('Failed to update booking via drag and drop:', error);
      let errorMessage = 'Failed to move booking. Please try again.';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setDragFeedback({ 
        type: 'error', 
        message: errorMessage 
      });
    },
  });

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  // Color palette for different properties
  const colorPalette = useMemo(() => [
    '#e57373', // Red
    '#f06292', // Pink
    '#ba68c8', // Purple
    '#9575cd', // Deep Purple
    '#7986cb', // Indigo
    '#64b5f6', // Blue
    '#4fc3f7', // Light Blue
    '#4dd0e1', // Cyan
    '#4db6ac', // Teal
    '#81c784', // Green
    '#aed581', // Light Green
    '#ffb74d', // Orange
    '#ff8a65', // Deep Orange
    '#a1887f', // Brown
    '#90a4ae', // Blue Grey
  ], []);

  // Get property color with better distribution for neighboring properties
  const getPropertyColor = useCallback((propertyId: number) => {
    // Check if there's a custom color for this property - prioritize custom colors
    if (customPropertyColors[propertyId]) {
      return customPropertyColors[propertyId];
    }
    
    // Create a sequence that maximizes distance between consecutive properties
    // This ensures neighboring properties have very different colors
    const sequence = [0, 8, 3, 11, 6, 14, 2, 9, 5, 12, 1, 10, 4, 13, 7];
    const index = sequence[propertyId % sequence.length];
    return colorPalette[index];
  }, [colorPalette, customPropertyColors]);

  // Get property text color
  const getPropertyTextColor = useCallback((propertyId: number) => {
    const color = customPropertyTextColors[propertyId];
    // If custom text color is set, use it, otherwise return undefined for default styling
    return color ? color : undefined;
  }, [customPropertyTextColors]);

  // Get unit background color (inherits from property)
  const getUnitColor = useCallback((unitId: number, propertyId: number) => {
    // If custom color is set, use it with higher opacity, otherwise use default color with lower opacity
    const propertyColor = getPropertyColor(propertyId);
    const hasCustomColor = customPropertyColors[propertyId];
    
    if (hasCustomColor) {
      // For custom colors, use 90% opacity for more prominent appearance
      return alpha(propertyColor, 0.9);
    } else {
      // For default colors, use very light opacity
      return alpha(propertyColor, 0.15);
    }
  }, [getPropertyColor, customPropertyColors]);

  // Get unit text color (inherits from property)
  const getUnitTextColor = useCallback((unitId: number, propertyId: number) => {
    return customPropertyTextColors[propertyId] || undefined; // Return undefined to use default theme color
  }, [customPropertyTextColors]);

  // Get booking color based on status and remaining amount
  const getBookingColor = useCallback((booking: Booking) => {
    // First check booking status
    if (booking.status === 'pending') {
      return '#616161'; // Dark grey for pending bookings
    }
    
    // For active bookings, use color based on remaining amount
    const totalAmount = Number(booking.total_amount || 0);
    const paidAmount = Number(booking.paid_amount || 0);
    const remainingAmount = Math.max(0, totalAmount - paidAmount);
    
    if (remainingAmount === 0) {
      // Remaining amount = 0 (fully paid or no amount due)
      return '#4caf50'; // Green
    } else if (remainingAmount === totalAmount && totalAmount > 0) {
      // Remaining amount = Total amount (nothing paid, but amount is due)
      return '#f44336'; // Red
    } else {
      // Remaining amount > 0 but < Total amount (partially paid)
      return '#ff9800'; // Yellow/Orange
    }
  }, []);

  // Get booking icon based on status
  const getBookingIcon = useCallback((booking: Booking) => {
    // Primary icon based on booking status only
    switch (booking.status) {
      case 'active':
        return <Business sx={{ fontSize: 14 }} />;
      case 'pending':
        return <CalendarToday sx={{ fontSize: 14 }} />;
      case 'completed':
        return <Group sx={{ fontSize: 14 }} />;
      default:
        return <Person sx={{ fontSize: 14 }} />;
    }
  }, []);

  // Get check-in status indicator
  const getCheckinIndicator = (booking: Booking) => {
    if (!booking.checkin_status) return null;

    switch (booking.checkin_status) {
      case 'checked_in':
        return (
          <Tooltip 
            key={`checkedin-${booking.id}`}
            title={t('calendar.checkedIn')}
            disableHoverListener={isDragging}
            disableFocusListener={isDragging}
            disableTouchListener={isDragging}
          >
            <Login sx={{ fontSize: 12, color: '#4caf50', ml: 0.5 }} />
          </Tooltip>
        );
      case 'checked_out':
        return (
          <Tooltip 
            key={`checkedout-${booking.id}`}
            title={t('calendar.checkedOut')}
            disableHoverListener={isDragging}
            disableFocusListener={isDragging}
            disableTouchListener={isDragging}
          >
            <Logout sx={{ fontSize: 12, color: '#ff9800', ml: 0.5 }} />
          </Tooltip>
        );
      case 'not_checked_in':
        // Only show pending check-in icon for active bookings
        if (booking.status === 'active') {
          return (
            <Tooltip 
              key={`awaitingcheckin-${booking.id}`}
              title={t('calendar.awaitingCheckIn')}
              disableHoverListener={isDragging}
              disableFocusListener={isDragging}
              disableTouchListener={isDragging}
            >
              <Key sx={{ fontSize: 12, color: '#ffc107', ml: 0.5 }} />
            </Tooltip>
          );
        }
        break;
    }
    return null;
  };

  // Process bookings for calendar display (exclude cancelled bookings)
  const calendarBookings = useMemo(() => {
    return bookings
      .filter(booking => booking.status !== 'cancelled') // Exclude cancelled bookings from calendar
      .map((booking): CalendarBooking => {
        const property = properties.find(p => p.id === booking.property_id);
        const unit = units.find(u => u.id === booking.unit_id);
        
        return {
          ...booking,
          color: getBookingColor(booking),
          icon: getBookingIcon(booking),
          displayName: booking.tenant_name,
          propertyName: property?.name || `Property ${booking.property_id}`,
          unitName: unit?.unit_number || `Unit ${booking.unit_id}`,
        };
      });
  }, [bookings, properties, units, getBookingColor, getBookingIcon]);

  // Get check-in status indicator for a unit
  const getUnitCheckinIndicator = (unit: Unit) => {
    // Find current active bookings for this unit
    const currentDate = new Date();
    const unitBookings = calendarBookings.filter(booking => 
      booking.unit_id === unit.id && 
      booking.status === 'active' &&
      new Date(booking.start_date) <= currentDate &&
      new Date(booking.end_date) >= currentDate
    );

    // If no current bookings, return null
    if (unitBookings.length === 0) return null;

    // Get the most recent booking's check-in status
    const currentBooking = unitBookings[0];
    if (!currentBooking.checkin_status) return null;

    switch (currentBooking.checkin_status) {
      case 'checked_in':
        return (
          <Tooltip 
            key={`unit-checkedin-${unit.id}`}
            title={`${t('calendar.checkedIn')} - ${currentBooking.tenant_name}`}
            disableHoverListener={isDragging}
            disableFocusListener={isDragging}
            disableTouchListener={isDragging}
          >
            <Login sx={{ fontSize: 14, color: '#4caf50', opacity: 0.8 }} />
          </Tooltip>
        );
      case 'checked_out':
        return (
          <Tooltip 
            key={`unit-checkedout-${unit.id}`}
            title={`${t('calendar.checkedOut')} - ${currentBooking.tenant_name}`}
            disableHoverListener={isDragging}
            disableFocusListener={isDragging}
            disableTouchListener={isDragging}
          >
            <Logout sx={{ fontSize: 14, color: '#ff9800', opacity: 0.8 }} />
          </Tooltip>
        );
      case 'not_checked_in':
        return (
          <Tooltip 
            key={`unit-awaitingcheckin-${unit.id}`}
            title={`${t('calendar.awaitingCheckIn')} - ${currentBooking.tenant_name}`}
            disableHoverListener={isDragging}
            disableFocusListener={isDragging}
            disableTouchListener={isDragging}
          >
            <Key sx={{ fontSize: 14, color: '#ffc107', opacity: 0.8 }} />
          </Tooltip>
        );
    }
    return null;
  };

  // Group units by property
  // Function to build hierarchical unit display
  const buildHierarchicalUnits = useCallback((units: Unit[]): Unit[] => {
    if (units.length === 0) return [];

    // Create a map of units by ID for quick lookup
    const unitMap = new Map<number, Unit>();
    units.forEach(unit => unitMap.set(unit.id, unit));

    // Find root units (units without parents or whose parents are not in this property)
    const rootUnits = units.filter(unit => !unit.parent_unit_id || !unitMap.has(unit.parent_unit_id));

    // Recursive function to build hierarchy
    const buildHierarchy = (parentUnits: Unit[]): Unit[] => {
      const result: Unit[] = [];
      
      // Sort parent units by sort_order
      const sortedParents = [...parentUnits].sort((a, b) => a.sort_order - b.sort_order);
      
      for (const parent of sortedParents) {
        // Add the parent unit
        result.push(parent);
        
        // Find children
        const children = units.filter(unit => unit.parent_unit_id === parent.id);
        
        // Only add children if parent is expanded
        const shouldShowChildren = children.length > 0 && expandedUnits.has(parent.id);
        
        if (shouldShowChildren) {
          const sortedChildren = buildHierarchy(children);
          result.push(...sortedChildren);
        }
      }
      
      return result;
    };

    return buildHierarchy(rootUnits);
  }, [expandedUnits]); // Added expandedUnits as dependency

  // Function to toggle unit expansion
  const toggleUnitExpansion = (unitId: number) => {
    setExpandedUnits(prev => {
      const newSet = new Set(prev);
      if (newSet.has(unitId)) {
        newSet.delete(unitId);
      } else {
        newSet.add(unitId);
      }
      return newSet;
    });
  };

  // Function to check if a unit has children
  const hasChildren = (unit: Unit, allUnits: Unit[]): boolean => {
    return allUnits.some(u => u.parent_unit_id === unit.id);
  };

  // Function to expand all units in a property
  const expandAllUnits = (propertyId: number) => {
    const propertyUnits = units.filter(unit => unit.property_id === propertyId);
    const parentUnits = propertyUnits.filter(unit => hasChildren(unit, propertyUnits));
    
    setExpandedUnits(prev => {
      const newSet = new Set(prev);
      parentUnits.forEach(unit => newSet.add(unit.id));
      return newSet;
    });
  };

  // Function to collapse all units in a property
  const collapseAllUnits = (propertyId: number) => {
    const propertyUnits = units.filter(unit => unit.property_id === propertyId);
    const parentUnits = propertyUnits.filter(unit => hasChildren(unit, propertyUnits));
    
    setExpandedUnits(prev => {
      const newSet = new Set(prev);
      parentUnits.forEach(unit => newSet.delete(unit.id));
      return newSet;
    });
  };

  // Function to get unit hierarchy display name
  const getUnitHierarchyName = (unit: Unit): string => {
    if (!unit.parent) {
      return unit.name;
    }
    
    const buildParentPath = (currentUnit: Unit): string => {
      if (!currentUnit.parent) {
        return currentUnit.name;
      }
      return `${buildParentPath(currentUnit.parent)} → ${currentUnit.name}`;
    };
    
    return buildParentPath(unit);
  };

  // Helper function to get hierarchical unit name by unit ID for booking details
  const getUnitHierarchyNameById = (unitId: number): string => {
    const unit = units.find(u => u.id === unitId);
    if (!unit) return 'Unknown Unit';
    
    // Build parent path using units array (since unit.parent might not be populated)
    const buildParentPath = (currentUnit: Unit): string => {
      const parent = units.find(u => u.id === currentUnit.parent_unit_id);
      if (!parent) return currentUnit.unit_number;
      
      const parentPath = buildParentPath(parent);
      return `${parentPath} → ${currentUnit.unit_number}`;
    };
    
    return buildParentPath(unit);
  };

  // Function to get indentation level based on unit hierarchy
  const getUnitIndentation = (unit: Unit): number => {
    return unit.level * 16; // 16px per level
  };

  // Function to get unit background color based on hierarchy (derived from parent)
  const getUnitBackgroundColor = (unit: Unit, propertyId: number): string => {
    // For root units, use the original unit color
    if (unit.level === 0 || !unit.parent) {
      return getUnitColor(unit.id, propertyId);
    }
    
    // For sub-units, derive color from parent but make it visually distinct
    const parentColor = getUnitBackgroundColor(unit.parent, propertyId);
    
    // Create a visually distinct shade based on the unit's level
    // Each level gets progressively lighter with reduced opacity for clear visual hierarchy
    const opacityReduction = unit.level * 0.2; // 20% less opacity per level for better distinction
    const finalOpacity = Math.max(0.3, 1 - opacityReduction); // Minimum opacity 0.3, but creates clear difference
    
    return alpha(parentColor, finalOpacity);
  };

  // Function to get unit border color based on hierarchy level (same as before)
  const getUnitBorderColor = (unit: Unit): string => {
    const colors = [
      'primary.main',      // Level 0 (root)
      'secondary.main',    // Level 1
      'success.main',      // Level 2
      'warning.main',      // Level 3
      'error.main',        // Level 4
      'info.main',         // Level 5+
    ];
    return colors[Math.min(unit.level, colors.length - 1)];
  };

  // Helper function to check if a unit is reserved within the date range
  const isUnitReserved = (unit: Unit, startDate: Date, endDate: Date): boolean => {
    return bookings.some(booking => 
      booking.unit_id === unit.id &&
      booking.status !== 'cancelled' &&
      new Date(booking.start_date) <= endDate &&
      new Date(booking.end_date) >= startDate
    );
  };

  // Helper function to get all parent units of a given unit
  const getParentUnits = (unit: Unit): Unit[] => {
    const parents: Unit[] = [];
    let currentUnit = unit;
    
    while (currentUnit.parent_unit_id) {
      const parentUnit = units.find(u => u.id === currentUnit.parent_unit_id);
      if (!parentUnit) break;
      
      parents.push(parentUnit);
      currentUnit = parentUnit;
    }
    
    return parents;
  };

  // Helper function to check if a unit or any of its ancestors are reserved
  const isUnitOrAncestorReserved = (unit: Unit, startDate: Date, endDate: Date): boolean => {
    // Check if the current unit is reserved
    if (isUnitReserved(unit, startDate, endDate)) {
      return true;
    }

    // Check if any ancestor is reserved
    let currentUnit = unit;
    while (currentUnit.parent_unit_id) {
      const parentUnit = units.find(u => u.id === currentUnit.parent_unit_id);
      if (!parentUnit) break;

      if (isUnitReserved(parentUnit, startDate, endDate)) {
        return true;
      }

      currentUnit = parentUnit;
    }

    return false;
  };

  // Helper function to check if a unit has any reserved descendants
  const hasReservedDescendants = (unit: Unit, startDate: Date, endDate: Date): boolean => {
    // Get all direct and indirect children of this unit
    const children = units.filter(u => {
      let currentUnit = u;
      while (currentUnit.parent_unit_id) {
        if (currentUnit.parent_unit_id === unit.id) {
          return true;
        }
        const parentUnit = units.find(parent => parent.id === currentUnit.parent_unit_id);
        if (!parentUnit) break;
        currentUnit = parentUnit;
      }
      return false;
    });

    // Check if any child is reserved
    return children.some(child => isUnitReserved(child, startDate, endDate));
  };

  // Helper function to check if a unit should be displayed as reserved (including descendants)
  const isUnitDisplayReserved = (unit: Unit, startDate: Date, endDate: Date): boolean => {
    // Unit is reserved if it's directly reserved OR has reserved descendants
    return isUnitReserved(unit, startDate, endDate) || hasReservedDescendants(unit, startDate, endDate);
  };

  // Filter units based on status and date range
  const filteredUnits = useMemo(() => {
    if (!showFilterBar || filterStatus === 'all') {
      return units;
    }

    const startDate = parseISO(filterStartDate);
    const endDate = parseISO(filterEndDate);

    return units.filter(unit => {
      if (filterStatus === 'reserved') {
        // For reserved filter, show only units that are reserved (including those with reserved descendants)
        return isUnitDisplayReserved(unit, startDate, endDate);
      } else if (filterStatus === 'non_reserved') {
        // For non-reserved filter, hide units that are reserved OR have reserved ancestors
        return !isUnitOrAncestorReserved(unit, startDate, endDate);
      }

      return true;
    });
  }, [units, bookings, showFilterBar, filterStatus, filterStartDate, filterEndDate]);

  // Scroll to start date when filter is applied
  useEffect(() => {
    if (showFilterBar && filterStatus !== 'all') {
      const startDate = parseISO(filterStartDate);
      scrollToDate(startDate);
    }
  }, [filterStatus, filterStartDate, filterEndDate, showFilterBar]);

  const groupedUnits = useMemo(() => {
    const grouped: { [propertyId: number]: { property: Property; units: Unit[] } } = {};
    
    // Filter properties based on showOnCalendar flag (default to true if not set)
    const visibleProperties = properties.filter(property => property.showOnCalendar !== false);
    
    visibleProperties.forEach(property => {
      const propertyUnits = filteredUnits.filter(unit => unit.property_id === property.id);
      
      // For "Single Unit" (house) properties, show them directly in the calendar
      if (property.type === 'house') {
        grouped[property.id] = {
          property,
          units: buildHierarchicalUnits(propertyUnits), // Apply hierarchical sorting
        };
      } else {
        // For other property types, only include if they have units
        if (propertyUnits.length > 0) {
          grouped[property.id] = {
            property,
            units: buildHierarchicalUnits(propertyUnits), // Apply hierarchical sorting
          };
        }
      }
    });
    
    return grouped;
  }, [properties, filteredUnits, buildHierarchicalUnits]); // buildHierarchicalUnits already includes expandedUnits dependency

  // Get bookings for a specific unit and date
  const getBookingForUnitAndDate = (unitId: number, date: Date) => {
    return calendarBookings.find(booking => {
      const startDate = parseISO(booking.start_date);
      const endDate = parseISO(booking.end_date);
      // End date should be exclusive (booking ends the day before check-out)
      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
      return booking.unit_id === unitId && date >= startDate && date <= adjustedEndDate;
    });
  };

  // Get bookings for a unit and date, including bookings from descendants
  const getBookingForUnitAndDateWithDescendants = (unitId: number, date: Date) => {
    // First check if the unit itself has a booking
    const directBooking = getBookingForUnitAndDate(unitId, date);
    if (directBooking) {
      return directBooking;
    }

    // If no direct booking, check if any descendants have bookings
    const unit = units.find(u => u.id === unitId);
    if (!unit) return null;

    // Get all descendants of this unit
    const descendants = units.filter(u => {
      let currentUnit = u;
      while (currentUnit.parent_unit_id) {
        if (currentUnit.parent_unit_id === unitId) {
          return true;
        }
        const parentUnit = units.find(parent => parent.id === currentUnit.parent_unit_id);
        if (!parentUnit) break;
        currentUnit = parentUnit;
      }
      return false;
    });

    // Find the first booking from any descendant
    for (const descendant of descendants) {
      const descendantBooking = getBookingForUnitAndDate(descendant.id, date);
      if (descendantBooking) {
        return descendantBooking;
      }
    }

    return null;
  };

  // Check if booking starts on this date
  const isBookingStart = (booking: CalendarBooking, date: Date) => {
    const startDate = parseISO(booking.start_date);
    // Use original start date without adjustment
    return isSameDay(startDate, date);
  };

  // Check if booking ends on this date
  const isBookingEnd = (booking: CalendarBooking, date: Date) => {
    const endDate = parseISO(booking.end_date);
    // Booking ends the day before check-out (check-out day is exclusive)
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
    return isSameDay(adjustedEndDate, date);
  };

  // Navigate months
  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  // Date navigation functions
  const goToToday = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    setFilterStartDate(today);
    setFilterEndDate(today);
  };

  const goToNextDay = () => {
    const startDate = parseISO(filterStartDate);
    const endDate = parseISO(filterEndDate);
    const nextStartDate = addDays(startDate, 1);
    const nextEndDate = addDays(endDate, 1);
    setFilterStartDate(format(nextStartDate, 'yyyy-MM-dd'));
    setFilterEndDate(format(nextEndDate, 'yyyy-MM-dd'));
  };

  const goToPreviousDay = () => {
    const startDate = parseISO(filterStartDate);
    const endDate = parseISO(filterEndDate);
    const prevStartDate = addDays(startDate, -1);
    const prevEndDate = addDays(endDate, -1);
    setFilterStartDate(format(prevStartDate, 'yyyy-MM-dd'));
    setFilterEndDate(format(prevEndDate, 'yyyy-MM-dd'));
  };

  // Filter toggle function
  const toggleFilter = () => {
    if (showFilterBar) {
      // Clear filter and hide filter bar
      setFilterStatus('all');
      setFilterStartDate(format(new Date(), 'yyyy-MM-dd'));
      setFilterEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
    setShowFilterBar(!showFilterBar);
  };
  
  // Ref to track the scroll timeout
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Scroll to today's date
  // State to track when we need to scroll to today after month change
  const [shouldScrollToToday, setShouldScrollToToday] = useState(false);

  const scrollToToday = () => {
    const today = new Date();
    
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // First, check if we need to change the month/year
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();
    
    // If month or year is different, change to today's month/year first
    if (currentMonth !== todayMonth || currentYear !== todayYear) {
      setCurrentDate(today);
      setShouldScrollToToday(true); // Mark that we need to scroll after the month changes
    } else {
      // If we're already in today's month, just scroll to today
      const todayIndex = calendarDays.findIndex(day => isSameDay(day, today));
      
      if (todayIndex !== -1) {
        // Calculate scroll position based on responsive cell width
        const scrollPosition = todayIndex * cellWidth - 200; // Center today with some offset
        
        // Scroll the day header to today
        if (dayHeaderScrollRef.current) {
          dayHeaderScrollRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
        
        // Scroll the calendar grid to today
        if (calendarScrollRef.current) {
          calendarScrollRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
      }
    }
  };

  // Scroll to a specific date
  const scrollToDate = (targetDate: Date) => {
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // First, check if we need to change the month/year
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const targetMonth = targetDate.getMonth();
    const targetYear = targetDate.getFullYear();
    
    // If month or year is different, change to target month/year first
    if (currentMonth !== targetMonth || currentYear !== targetYear) {
      setCurrentDate(targetDate);
      // We'll scroll after the month changes
      scrollTimeoutRef.current = setTimeout(() => {
        scrollToDate(targetDate);
      }, 100);
    } else {
      // If we're already in the target month, just scroll to the date
      const targetIndex = calendarDays.findIndex(day => isSameDay(day, targetDate));
      
      if (targetIndex !== -1) {
        // Calculate scroll position based on responsive cell width
        const scrollPosition = targetIndex * cellWidth - 200; // Center the date with some offset
        
        // Scroll the day header to the target date
        if (dayHeaderScrollRef.current) {
          dayHeaderScrollRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
        
        // Scroll the calendar grid to the target date
        if (calendarScrollRef.current) {
          calendarScrollRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
      }
    }
  };

  // Handle cell hover events
  const handleCellMouseEnter = (date: Date, unitId: number, propertyId: number) => {
    setHoveredCellInfo({ date, unitId, propertyId });
  };

  const handleCellMouseLeave = () => {
    setHoveredCellInfo({ date: null, unitId: null, propertyId: null });
  };

  // Get display text for hovered cell
  const getHoveredCellDisplayText = () => {
    if (!hoveredCellInfo.date) return null;

    const date = hoveredCellInfo.date;
    const unitId = hoveredCellInfo.unitId;
    const propertyId = hoveredCellInfo.propertyId;
    
    const dayName = format(date, 'EEEE');
    const monthName = format(date, 'MMMM');
    const day = format(date, 'd');
    const year = format(date, 'yyyy');
    
    // Get the correct currency symbol for the property
    let currencySymbol = getMainCurrencySymbol();
    if (propertyId) {
      const property = properties.find(p => p.id === propertyId);
      if (property?.currency?.symbol) {
        currencySymbol = property.currency.symbol;
      }
    }
    
    let rateText = `Rate: ${currencySymbol}0`;
    if (unitId) {
      const rate = getRateForDay(date, unitId);
      rateText = `Rate: ${currencySymbol}${rate}`;
    }
    
    return `${dayName}, ${monthName} ${day}, ${year} ${rateText}`;
  };



  const handleBookingClick = async (booking: CalendarBooking) => {
    console.log('Booking clicked:', booking);
    setShowBookingDialog(true);
    
    // Fetch fresh booking data to ensure we have the latest information including auto_cancel_at
    try {
      const freshBookingData = await bookingApi.getById(booking.id);
      console.log('Fresh booking data:', freshBookingData);
      // Convert to CalendarBooking format with the fresh data
      const freshCalendarBooking: CalendarBooking = {
        ...freshBookingData,
        color: booking.color,
        icon: booking.icon,
        displayName: booking.displayName,
        propertyName: booking.propertyName,
        unitName: booking.unitName,
      };
      setSelectedBooking(freshCalendarBooking);
    } catch (error) {
      console.error('Failed to fetch fresh booking data:', error);
      // Fallback to the existing booking data
      setSelectedBooking(booking);
    }
    
    // Load booking history
    setLoadingHistory(true);
    try {
      console.log('Loading booking history for booking ID:', booking.id);
      const history = await bookingApi.getHistory(booking.id);
      console.log('Booking history loaded:', history);
      setBookingHistory(history);
    } catch (error) {
      console.error('Failed to load booking history:', error);
      setBookingHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: React.DragEvent<HTMLElement>, booking: CalendarBooking) => {
    console.log('Drag started for booking:', booking);
    setDraggedBooking(booking);
    setIsDragging(true);
    
    event.dataTransfer.effectAllowed = 'move';
    
    // Calculate and store the mouse offset within the booking bar
    const originalElement = event.currentTarget;
    const elementRect = originalElement.getBoundingClientRect();
    const mouseX = event.clientX;
    const elementLeft = elementRect.left;
    const offsetWithinBar = mouseX - elementLeft;
    
    // Store the offset in a way we can access it during drop
    // We'll use dataTransfer to store this information
    event.dataTransfer.setData('text/plain', JSON.stringify({
      bookingId: booking.id,
      offsetWithinBar: offsetWithinBar,
      barWidth: elementRect.width
    }));
    
    // Create custom drag image with proper dimensions
    const dragElement = originalElement.cloneNode(true) as HTMLElement;
    
    // Set exact dimensions to match original
    dragElement.style.width = `${elementRect.width}px`;
    dragElement.style.height = `${elementRect.height}px`;
    dragElement.style.maxHeight = `${elementRect.height}px`;
    dragElement.style.minHeight = `${elementRect.height}px`;
    
    // Position off-screen
    dragElement.style.position = 'fixed';
    dragElement.style.top = '-1000px';
    dragElement.style.left = '-1000px';
    dragElement.style.zIndex = '9999';
    
    // Style the drag image
    dragElement.style.backgroundColor = alpha(getBookingColor(booking), 0.8);
    dragElement.style.transform = 'rotate(2deg)';
    dragElement.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
    dragElement.style.borderRadius = '14px';
    dragElement.style.border = '2px solid white';
    dragElement.style.overflow = 'hidden';
    
    // Ensure text and content stay the same size
    const textElements = dragElement.querySelectorAll('*');
    textElements.forEach((el: any) => {
      if (el.style) {
        el.style.fontSize = 'inherit';
        el.style.lineHeight = 'inherit';
      }
    });
    
    document.body.appendChild(dragElement);
    event.dataTransfer.setDragImage(dragElement, elementRect.width / 2, elementRect.height / 2);
    
    // Clean up the temporary element
    setTimeout(() => {
      if (document.body.contains(dragElement)) {
        document.body.removeChild(dragElement);
      }
    }, 0);
  };

  const handleDragEnd = () => {
    console.log('Drag ended');
    setDraggedBooking(null);
    setIsDragging(false);
    setDragOverCell(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>, date: Date, unitId: number, propertyId: number) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    
    // Update drag over cell
    setDragOverCell({ date, unitId, propertyId });
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = async (event: React.DragEvent<HTMLElement>, date: Date, unitId: number, propertyId: number) => {
    event.preventDefault();
    
    if (!draggedBooking) return;
    
    console.log('Drop event:', { date, unitId, propertyId, draggedBooking });
    
    // Clear drag state
    setDragOverCell(null);
    
    // Get the stored drag information
    let dragInfo = null;
    try {
      const dragData = event.dataTransfer.getData('text/plain');
      if (dragData) {
        dragInfo = JSON.parse(dragData);
      }
    } catch (error) {
      console.log('Could not parse drag data, using fallback');
    }
    
    // Calculate the mouse position within the dropped cell
    const dropElement = event.currentTarget;
    const dropRect = dropElement.getBoundingClientRect();
    const mouseX = event.clientX;
    const cellLeft = dropRect.left;
    const mouseOffsetInCell = mouseX - cellLeft;
    
    // Each day cell is 40px wide
    const cellWidth = 40;
    let daysToSubtract = 0;
    
    if (dragInfo && dragInfo.offsetWithinBar !== undefined) {
      // Calculate how many days to move back based on where the mouse was in the original bar
      // The offset should represent which day within the booking the mouse was on
      const dayOffsetInBar = Math.floor(dragInfo.offsetWithinBar / cellWidth);
      daysToSubtract = dayOffsetInBar;
      
      console.log('Drag calculation:', {
        offsetWithinBar: dragInfo.offsetWithinBar,
        barWidth: dragInfo.barWidth,
        cellWidth,
        dayOffsetInBar,
        daysToSubtract,
        mouseOffsetInCell
      });
    }
    
    // Calculate new start and end dates based on booking duration
    const originalStart = parseISO(draggedBooking.start_date);
    const originalEnd = parseISO(draggedBooking.end_date);
    
    // Calculate the duration in days - end date is exclusive (check-out day)
    // So a booking from Jan 1 to Jan 3 means Jan 1 (check-in) and Jan 2 (night), check-out on Jan 3
    const bookingDurationDays = Math.round((originalEnd.getTime() - originalStart.getTime()) / (1000 * 60 * 60 * 24));
    
    // The dropped date becomes the new start date, adjusted for where the mouse was in the bar
    const adjustedStartDate = addDays(date, -daysToSubtract);
    const newStartDate = adjustedStartDate;
    const newEndDate = addDays(newStartDate, bookingDurationDays);
    
    console.log('Date calculation:', {
      originalStart: originalStart.toISOString().split('T')[0],
      originalEnd: originalEnd.toISOString().split('T')[0],
      bookingDurationDays,
      newStartDate: newStartDate.toISOString().split('T')[0],
      newEndDate: newEndDate.toISOString().split('T')[0],
      droppedDate: date.toISOString().split('T')[0]
    });
    
    // Check if this is actually a change
    const isSameStart = isSameDay(originalStart, newStartDate);
    const isSameUnit = draggedBooking.unit_id === unitId;
    const isSameProperty = draggedBooking.property_id === propertyId;
    
    if (isSameStart && isSameUnit && isSameProperty) {
      console.log('No change detected, ignoring drop');
      return;
    }
    
    // Prepare update data - ensure dates are in local timezone format (YYYY-MM-DD)
    const updateData: any = {
      start_date: format(newStartDate, 'yyyy-MM-dd'),
      end_date: format(newEndDate, 'yyyy-MM-dd'),
    };
    
    // Only update unit and property if they changed
    if (!isSameUnit) {
      updateData.unit_id = unitId;
    }
    
    if (!isSameProperty) {
      updateData.property_id = propertyId;
    }
    
    console.log('Updating booking with:', updateData);
    
    try {
      await updateBookingMutation.mutateAsync({
        bookingId: draggedBooking.id,
        updateData,
        originalBooking: draggedBooking,
        newPropertyId: !isSameProperty ? propertyId : undefined
      });
    } catch (error) {
      console.error('Failed to update booking:', error);
    }
  };

  const isDragOverCurrentCell = (date: Date, unitId: number, propertyId: number) => {
    return dragOverCell && 
           isSameDay(dragOverCell.date, date) && 
           dragOverCell.unitId === unitId && 
           dragOverCell.propertyId === propertyId;
  };

  const handleCloseBookingDialog = () => {
    setShowBookingDialog(false);
    setSelectedBooking(null);
    setBookingHistory([]);
  };

  // Handle payment dialog
  const handleOpenPaymentDialog = () => {
    setPaymentAmount('');
    setShowPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setShowPaymentDialog(false);
    setPaymentAmount('');
  };

  const handleAddPayment = async () => {
    if (!selectedBooking || !paymentAmount || isNaN(Number(paymentAmount)) || Number(paymentAmount) <= 0) {
      return;
    }

    // Prevent double execution
    if (paymentLoading) {
      return;
    }

    setPaymentLoading(true);
    try {
      const paymentAmountNum = Number(paymentAmount);
      
      // Use the dedicated payment endpoint
      const response = await bookingApi.addPayment(selectedBooking.id, paymentAmountNum);

      // Update the selected booking to reflect the change
      setSelectedBooking(prev => prev ? {
        ...prev,
        ...response.booking
      } : null);

      // Invalidate and refetch bookings to ensure UI consistency
      queryClient.invalidateQueries({ queryKey: ['bookings'] });

      // Close the payment dialog
      handleClosePaymentDialog();
      
      // Optionally trigger a refresh of the bookings data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to add payment:', error);
      // You might want to show an error message to the user here
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle edit payment dialog
  const handleOpenEditPaymentDialog = (paymentHistory: BookingHistory) => {
    // Extract payment amount from description
    const descriptionMatch = paymentHistory.description.match(/Payment (?:of )?(\d+\.?\d*)/i);
    const paymentAmount = descriptionMatch ? descriptionMatch[1] : '0';
    
    setEditingPaymentHistory(paymentHistory);
    setEditPaymentAmount(paymentAmount);
    setEditPaymentReason('Payment amount corrected');
    setShowEditPaymentDialog(true);
  };

  const handleCloseEditPaymentDialog = () => {
    setShowEditPaymentDialog(false);
    setEditingPaymentHistory(null);
    setEditPaymentAmount('');
    setEditPaymentReason('');
  };

  const handleEditPayment = async () => {
    if (!editingPaymentHistory || !selectedBooking || !editPaymentAmount || isNaN(Number(editPaymentAmount)) || Number(editPaymentAmount) < 0) {
      return;
    }

    // Prevent double execution
    if (editPaymentLoading) {
      return;
    }

    setEditPaymentLoading(true);
    try {
      // Extract original payment amount from description
      const descriptionMatch = editingPaymentHistory.description.match(/Payment (?:of )?(\d+\.?\d*)/i);
      const originalAmount = descriptionMatch ? Number(descriptionMatch[1]) : 0;
      const newAmount = Number(editPaymentAmount);
      
      const response = await bookingApi.modifyPayment(selectedBooking.id, originalAmount, newAmount, editPaymentReason);

      // Update the selected booking to reflect the change
      if (response.booking) {
        setSelectedBooking({
          ...selectedBooking,
          ...response.booking,
        });
      }

      // Invalidate and refetch bookings to ensure UI consistency
      queryClient.invalidateQueries({ queryKey: ['bookings'] });

      // Reload booking history
      setLoadingHistory(true);
      try {
        const history = await bookingApi.getHistory(selectedBooking.id);
        setBookingHistory(history);
      } catch (error) {
        console.error('Failed to reload booking history:', error);
        setBookingHistory([]);
      } finally {
        setLoadingHistory(false);
      }

      setShowEditPaymentDialog(false);
      setEditingPaymentHistory(null);
      setEditPaymentAmount('');
      setEditPaymentReason('');

    } catch (error) {
      console.error('Error modifying payment:', error);
    } finally {
      setEditPaymentLoading(false);
    }
  };

  // Handle property color change - open comprehensive color edit dialog
  const handlePropertyColorClick = (event: React.MouseEvent<HTMLElement>, propertyId: number) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Find the property object
    const property = properties.find(p => p.id === propertyId);
    if (!property) return;
    
    // Set the property for the color edit dialog
    setSelectedPropertyForMenu(property);
    
    // Get current colors - custom colors take precedence over defaults
    const currentBgColor = customPropertyColors[propertyId] || getPropertyColor(propertyId);
    const currentTextColor = customPropertyTextColors[propertyId] || '#666666';
    
    setTempBackgroundColor(convertToHex(currentBgColor));
    setTempTextColor(convertToHex(currentTextColor));
    setShowColorEditDialog(true);
  };

  const handleColorChange = (color: string) => {
    if (selectedPropertyForColor !== null) {
      updateColorMutation.mutate({
        propertyId: selectedPropertyForColor,
        background_color: color,
        text_color: customPropertyTextColors[selectedPropertyForColor] || null,
      });
    }
    setShowColorPicker(false);
    setSelectedPropertyForColor(null);
    setColorPickerAnchor(null);
  };

  const handleCloseColorPicker = () => {
    setShowColorPicker(false);
    setSelectedPropertyForColor(null);
    setColorPickerAnchor(null);
  };

  // Handle property menu
  const handlePropertyMenuClick = (event: React.MouseEvent<HTMLElement>, property: Property) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Get the button element directly
    const buttonElement = event.currentTarget;
    
    setSelectedPropertyForMenu(property);
    setPropertyMenuAnchor(buttonElement);
  };

  const handleClosePropertyMenu = () => {
    setPropertyMenuAnchor(null);
    setSelectedPropertyForMenu(null);
  };

  // Handle unit menu
  const handleUnitMenuClick = (event: React.MouseEvent<HTMLElement>, unit: Unit) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedUnitForMenu(unit);
    // Also set the property context for the unit
    const unitProperty = properties.find(p => p.id === unit.property_id);
    setSelectedPropertyForMenu(unitProperty || null);
    setUnitMenuAnchor(event.currentTarget);
  };

  // Handle single unit property menu (for properties of type 'house')
  const handleSingleUnitMenuClick = (event: React.MouseEvent<HTMLElement>, property: Property) => {
    event.preventDefault();
    event.stopPropagation();
    // For single unit properties, we treat the property as both property and unit
    setSelectedPropertyForMenu(property);
    setSelectedUnitForMenu(null); // No specific unit for single unit properties
    setUnitMenuAnchor(event.currentTarget);
  };

  const handleCloseUnitMenu = () => {
    setUnitMenuAnchor(null);
    setSelectedUnitForMenu(null);
  };

  // Menu action handlers
  const handleQuickDetails = (type: 'property' | 'unit') => {
    if (type === 'property' && selectedPropertyForMenu) {
      navigate(`/properties/${selectedPropertyForMenu.id}`);
    } else if (type === 'unit' && selectedUnitForMenu) {
      navigate(`/units/${selectedUnitForMenu.id}`);
    }
    handleClosePropertyMenu();
    handleCloseUnitMenu();
  };

  const handleExpenses = (type: 'property' | 'unit') => {
    if (type === 'property' && selectedPropertyForMenu) {
      setExpenseProperty(selectedPropertyForMenu);
      setExpenseUnit(null);
      setShowExpenseDialog(true);
    } else if (type === 'unit' && selectedUnitForMenu) {
      // For unit expenses, we need both the property and the unit
      setExpenseProperty(selectedPropertyForMenu);
      setExpenseUnit(selectedUnitForMenu);
      setShowExpenseDialog(true);
    }
    handleClosePropertyMenu();
    handleCloseUnitMenu();
  };

  const handlePurchaseInvoice = (type: 'property' | 'unit') => {
    if (type === 'property' && selectedPropertyForMenu) {
      setPurchaseInvoiceProperty(selectedPropertyForMenu);
      setPurchaseInvoiceUnit(null);
      setShowPurchaseInvoiceDialog(true);
    } else if (type === 'unit' && selectedUnitForMenu) {
      // For unit purchase invoices, we need both the property and the unit
      setPurchaseInvoiceProperty(selectedPropertyForMenu);
      setPurchaseInvoiceUnit(selectedUnitForMenu);
      setShowPurchaseInvoiceDialog(true);
    }
    handleClosePropertyMenu();
    handleCloseUnitMenu();
  };

  const handleReport = (type: 'property' | 'unit') => {
    if (type === 'property' && selectedPropertyForMenu) {
      // TODO: Implement property report
    } else if (type === 'unit' && selectedUnitForMenu) {
      // TODO: Implement unit report
    }
    handleClosePropertyMenu();
    handleCloseUnitMenu();
  };

  const handleCheckinCheckout = () => {
    console.log('Handling check-in/checkout for unit:', selectedUnitForMenu);
    if (selectedUnitForMenu) {
      // Find current active booking for this unit
      const currentDate = new Date();
      const activeBooking = calendarBookings.find(booking => 
        booking.unit_id === selectedUnitForMenu.id && 
        booking.status === 'active' &&
        new Date(booking.start_date) <= currentDate &&
        new Date(booking.end_date) >= currentDate
      );

      console.log('Active booking found:', activeBooking);
      if (activeBooking) {
        setSelectedBookingForCheckin(activeBooking);
        setCheckinStatus(activeBooking.checkin_status || 'not_checked_in');
        setShowCheckinDialog(true);
      } else {
        console.log('No active booking found for unit');
        // TODO: Show snackbar message that no active booking exists
      }
    }
    handleCloseUnitMenu();
  };

  const handleCleaning = () => {
    console.log('Handling cleaning for unit:', selectedUnitForMenu);
    if (selectedUnitForMenu) {
      setSelectedUnitForCleaning(selectedUnitForMenu);
      setCleaningStatus(selectedUnitForMenu.cleaning_status || 'cleaned');
      setShowCleaningDialog(true);
    }
    handleCloseUnitMenu();
  };

  const handleCloseCheckinDialog = () => {
    setShowCheckinDialog(false);
    setSelectedBookingForCheckin(null);
    setCheckinStatus('not_checked_in');
  };

  const handleSaveCheckinStatus = () => {
    if (!selectedBookingForCheckin) return;

    setCheckinLoading(true);
    
    // Prepare check-in data
    const checkinData: any = { checkin_status: checkinStatus };
    
    if (checkinStatus === 'checked_in') {
      checkinData.checkin_time = new Date().toISOString();
      checkinData.checkout_time = null;
    } else if (checkinStatus === 'checked_out') {
      checkinData.checkout_time = new Date().toISOString();
      // Keep existing checkin_time if it exists
      if (!selectedBookingForCheckin.checkin_time) {
        checkinData.checkin_time = new Date().toISOString();
      }
    } else if (checkinStatus === 'not_checked_in') {
      checkinData.checkin_time = null;
      checkinData.checkout_time = null;
    }

    // Update booking check-in status
    updateBookingCheckinMutation.mutate({
      bookingId: selectedBookingForCheckin.id,
      checkinData
    }, {
      onSuccess: () => {
        // If checking in, automatically set unit cleaning status to "not_cleaned"
        if (checkinStatus === 'checked_in' && units) {
          const unit = units.find(u => u.id === selectedBookingForCheckin.unit_id);
          if (unit && unit.cleaning_status !== 'not_cleaned') {
            updateUnitCleaningMutation.mutate({
              unitId: unit.id,
              cleaningStatus: 'not_cleaned'
            }, {
              onSuccess: () => {
                // Small delay to ensure cache updates propagate
                setTimeout(() => {
                  handleCloseCheckinDialog();
                  setCheckinLoading(false);
                }, 100);
              },
              onError: (error) => {
                console.error('Failed to update unit cleaning status:', error);
                setCheckinLoading(false);
              }
            });
          } else {
            handleCloseCheckinDialog();
            setCheckinLoading(false);
          }
        } else {
          handleCloseCheckinDialog();
          setCheckinLoading(false);
        }
      },
      onError: (error) => {
        console.error('Failed to update check-in status:', error);
        setCheckinLoading(false);
      }
    });
  };

  // Close cleaning dialog
  const handleCloseCleaningDialog = () => {
    setShowCleaningDialog(false);
    setSelectedUnitForCleaning(null);
    setCleaningStatus('cleaned');
  };

  // Save cleaning status
  const handleSaveCleaningStatus = () => {
    if (!selectedUnitForCleaning) return;

    // Optimistic update: immediately update the cache before API call
    queryClient.setQueryData(['units'], (oldUnits: any[]) => {
      if (!oldUnits) return oldUnits;
      
      return oldUnits.map(unit => {
        if (unit.id === selectedUnitForCleaning.id) {
          return {
            ...unit,
            cleaning_status: cleaningStatus
          };
        }
        return unit;
      });
    });

    // IMPORTANT: Also optimistically update units-all cache (used by BookingsPage)
    queryClient.setQueryData(['units-all'], (oldUnits: any[]) => {
      if (!oldUnits) return oldUnits;
      
      return oldUnits.map(unit => {
        if (unit.id === selectedUnitForCleaning.id) {
          return {
            ...unit,
            cleaning_status: cleaningStatus
          };
        }
        return unit;
      });
    });

    // Also optimistically update bookings cache
    queryClient.setQueryData(['bookings'], (oldBookings: any[]) => {
      if (!oldBookings) return oldBookings;
      
      return oldBookings.map(booking => {
        if (booking.unit_id === selectedUnitForCleaning.id) {
          return {
            ...booking,
            unit: booking.unit ? {
              ...booking.unit,
              cleaning_status: cleaningStatus
            } : booking.unit
          };
        }
        return booking;
      });
    });

    setCleaningLoading(true);
    updateUnitCleaningMutation.mutate({
      unitId: selectedUnitForCleaning.id,
      cleaningStatus: cleaningStatus
    }, {
      onSuccess: (data) => {
        // Close dialog immediately since optimistic update already happened
        handleCloseCleaningDialog();
        setCleaningLoading(false);
      },
      onError: (error) => {
        console.error('Failed to update unit cleaning status:', error);
        
        // Revert optimistic update on error
        queryClient.setQueryData(['units'], (oldUnits: any[]) => {
          if (!oldUnits) return oldUnits;
          
          return oldUnits.map(unit => {
            if (unit.id === selectedUnitForCleaning.id) {
              return {
                ...unit,
                cleaning_status: selectedUnitForCleaning.cleaning_status // Revert to original
              };
            }
            return unit;
          });
        });
        
        // Also revert units-all cache
        queryClient.setQueryData(['units-all'], (oldUnits: any[]) => {
          if (!oldUnits) return oldUnits;
          
          return oldUnits.map(unit => {
            if (unit.id === selectedUnitForCleaning.id) {
              return {
                ...unit,
                cleaning_status: selectedUnitForCleaning.cleaning_status // Revert to original
              };
            }
            return unit;
          });
        });
        
        setCleaningLoading(false);
        // Keep dialog open on error so user can retry
      }
    });
  };

  // Helper function to convert any color to hex format
  const convertToHex = (color: string): string => {
    // If already hex, return as is
    if (color.startsWith('#')) {
      return color;
    }
    
    // Convert rgba/rgb to hex (basic implementation)
    if (color.startsWith('rgb')) {
      // For rgba colors, extract the rgb values and convert to hex
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]).toString(16).padStart(2, '0');
        const g = parseInt(matches[1]).toString(16).padStart(2, '0');
        const b = parseInt(matches[2]).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
      }
    }
    
    // Default fallbacks
    return color === 'textSecondary' ? '#666666' : '#ffffff';
  };

  // Handle color edit (only for properties)
  const handleEditColors = () => {
    if (selectedPropertyForMenu) {
      // Get current colors - custom colors take precedence over defaults
      const currentBgColor = customPropertyColors[selectedPropertyForMenu.id] || getPropertyColor(selectedPropertyForMenu.id);
      const currentTextColor = customPropertyTextColors[selectedPropertyForMenu.id] || '#666666';
      
      setTempBackgroundColor(convertToHex(currentBgColor));
      setTempTextColor(convertToHex(currentTextColor));
      setShowColorEditDialog(true);
    }
    handleClosePropertyMenu();
  };

  const handleSaveColors = () => {
    if (selectedPropertyForMenu) {
      updateColorMutation.mutate({
        propertyId: selectedPropertyForMenu.id,
        background_color: tempBackgroundColor || null,
        text_color: tempTextColor || null,
      });
    }
    
    setShowColorEditDialog(false);
    setTempBackgroundColor('');
    setTempTextColor('');
  };

  const handleCancelColorEdit = () => {
    setShowColorEditDialog(false);
    setTempBackgroundColor('');
    setTempTextColor('');
  };

  // Validate and format hex color
  const validateHexColor = (color: string): string => {
    // Remove any whitespace and convert to lowercase
    color = color.trim().toLowerCase();
    
    // Add # if missing
    if (!color.startsWith('#')) {
      color = '#' + color;
    }
    
    // Validate hex format
    const hexRegex = /^#[0-9a-f]{6}$/i;
    if (hexRegex.test(color)) {
      return color;
    }
    
    // If invalid, return a default color
    return '#ffffff';
  };

  // Handle color input changes
  const handleBackgroundColorChange = (value: string) => {
    setTempBackgroundColor(value);
  };

  const handleTextColorChange = (value: string) => {
    setTempTextColor(value);
  };

  // Field name mapping for display purposes
  const getFieldDisplayName = (fieldName: string): string => {
    const fieldMapping: { [key: string]: string } = {
      'paid_amount': 'total_paid',
      'total_amount': 'total_amount',
      'payment_status': 'payment_status',
      'start_date': 'start_date',
      'end_date': 'end_date',
      'guest_name': 'guest_name',
      'guest_email': 'guest_email',
      'guest_phone': 'guest_phone',
      'notes': 'notes',
      'status': 'status'
    };
    
    return fieldMapping[fieldName] || fieldName;
  };

  // Helper functions for ticket dialog
  const handleShareTicket = async () => {
    if (!selectedBooking) return;
    
    // Extract plain text from HTML content
    const getPlainTextFromHtml = (html: string) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    };
    
    const baseTicketText = ticketLanguage === 'ar' 
      ? `تذكرة الحجز
رقم الحجز: ${selectedBooking.booking_number || selectedBooking.id}
الاسم: ${selectedBooking.tenant_name}
رقم الموبايل: ${selectedBooking.tenant_phone || 'غير محدد'}
العقار المؤجر: ${selectedBooking.property?.name} - ${getUnitHierarchyNameById(selectedBooking.unit_id)}
تاريخ الدخول: ${new Date(selectedBooking.start_date).toLocaleDateString('ar-EG')}
تاريخ المغادرة: ${new Date(selectedBooking.end_date).toLocaleDateString('ar-EG')}
عدد الليالي: ${Math.ceil((new Date(selectedBooking.end_date).getTime() - new Date(selectedBooking.start_date).getTime()) / (1000 * 60 * 60 * 24))} ليلة
اجمالي الايجار: ${selectedBooking.rent_amount} ${selectedBooking.currency?.symbol || 'ر.ع'}
المبلغ المدفوع: ${selectedBooking.paid_amount || 0} ${selectedBooking.currency?.symbol || 'ر.ع'}
الباقي: ${(() => {
  const totalRent = selectedBooking.rent_amount || 0;
  const paidAmount = selectedBooking.paid_amount || 0;
  const remaining = totalRent - paidAmount;
  return remaining.toFixed(2);
})()} ${selectedBooking.currency?.symbol || 'ر.ع'}`
      : `Booking Ticket
Booking Number: ${selectedBooking.booking_number || selectedBooking.id}
Name: ${selectedBooking.tenant_name}
Phone: ${selectedBooking.tenant_phone || 'Not specified'}
Property: ${selectedBooking.property?.name} - ${getUnitHierarchyNameById(selectedBooking.unit_id)}
Check-in: ${new Date(selectedBooking.start_date).toLocaleDateString('en-US')}
Check-out: ${new Date(selectedBooking.end_date).toLocaleDateString('en-US')}
Nights: ${Math.ceil((new Date(selectedBooking.end_date).getTime() - new Date(selectedBooking.start_date).getTime()) / (1000 * 60 * 60 * 24))} nights
Total Rent: ${selectedBooking.rent_amount} ${selectedBooking.currency?.symbol || 'OMR'}
Paid Amount: ${selectedBooking.paid_amount || 0} ${selectedBooking.currency?.symbol || 'OMR'}
Remaining: ${(() => {
  const totalRent = selectedBooking.rent_amount || 0;
  const paidAmount = selectedBooking.paid_amount || 0;
  const remaining = totalRent - paidAmount;
  return remaining.toFixed(2);
})()} ${selectedBooking.currency?.symbol || 'OMR'}`;

    // Add document content if available
    const documentContent = ticketDocumentContent ? getPlainTextFromHtml(ticketDocumentContent) : '';
    const fullTicketText = documentContent ? `${baseTicketText}\n\n${documentContent}` : baseTicketText;

    if (navigator.share) {
      try {
        await navigator.share({
          title: ticketLanguage === 'ar' ? 'تذكرة الحجز' : 'Booking Ticket',
          text: fullTicketText,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(fullTicketText);
        alert(ticketLanguage === 'ar' ? 'تم نسخ التذكرة إلى الحافظة' : 'Ticket copied to clipboard');
      } catch (error) {
        console.log('Error copying to clipboard:', error);
      }
    }
  };

  const toggleTicketLanguage = () => {
    setTicketLanguage(prev => prev === 'ar' ? 'en' : 'ar');
  };

  // Function to fetch ticket documents (using same approach as Settings page)
  const fetchTicketDocuments = async () => {
    try {
      console.log('🔍 Fetching ticket documents...');
      const documents = await documentsApi.getAll();
      console.log('📄 Documents response:', documents);
      setTicketDocuments(documents || []);
      console.log('✅ Ticket documents fetched:', (documents || []).length, 'documents');
    } catch (error) {
      console.error('❌ Error fetching ticket documents:', error);
      setTicketDocuments([]);
    }
  };

  // Function to fetch and set the appropriate ticket document content
  const fetchTicketDocumentContent = async (language: 'ar' | 'en') => {
    try {
      console.log('🔍 Fetching ticket document content for language:', language);
      
      if (!ticketDocuments || ticketDocuments.length === 0) {
        console.log('📄 No ticket documents available');
        setTicketDocumentContent('');
        return;
      }
      
      const documentTitle = language === 'ar' ? 'Arabic Ticket' : 'English Ticket';
      console.log('🔍 Looking for document with title:', documentTitle);
      console.log('🔍 Available documents:', ticketDocuments.map(d => ({ id: d.id, title: d.title })));
      
      // Try exact match first
      let document = ticketDocuments.find(doc => doc.title === documentTitle);
      
      // If exact match fails, try case-insensitive search
      if (!document) {
        console.log('🔍 Exact match failed, trying case-insensitive search...');
        document = ticketDocuments.find(doc => 
          doc.title.toLowerCase() === documentTitle.toLowerCase()
        );
      }
      
      // If still no match, try partial match
      if (!document) {
        console.log('🔍 Case-insensitive match failed, trying partial match...');
        const searchTerm = language === 'ar' ? 'arabic' : 'english';
        document = ticketDocuments.find(doc => 
          doc.title.toLowerCase().includes(searchTerm) && 
          doc.title.toLowerCase().includes('ticket')
        );
      }
      
      console.log('📄 Found document:', document);
      
      if (document) {
        console.log('📄 Fetching content for document ID:', document.id);
        const response = await documentsApi.getById(document.id);
        console.log('📄 Document content response:', response);
        const content = response.content || '';
        setTicketDocumentContent(content);
        console.log('✅ Ticket document content set:', content.length, 'characters');
        if (content.length > 0) {
          console.log('📄 Content preview:', content.substring(0, 100) + '...');
        }
      } else {
        console.log('❌ Document not found for title:', documentTitle);
        console.log('❌ Available document titles:', ticketDocuments.map(d => d.title));
        setTicketDocumentContent('');
      }
    } catch (error) {
      console.error('❌ Error fetching ticket document content:', error);
      setTicketDocumentContent('');
    }
  };

  // Effect to fetch ticket documents when component mounts
  React.useEffect(() => {
    fetchTicketDocuments();
  }, []);

  // Effect to fetch document content when ticket language changes
  React.useEffect(() => {
    if (ticketDocuments && ticketDocuments.length > 0) {
      fetchTicketDocumentContent(ticketLanguage);
    }
  }, [ticketLanguage, ticketDocuments]);

  // Synchronized scrolling handlers
  const handleUnitsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (calendarScrollRef.current && e.currentTarget.scrollTop !== calendarScrollRef.current.scrollTop) {
      calendarScrollRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  const handleCalendarScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Synchronize vertical scroll with units list
    if (unitsScrollRef.current && e.currentTarget.scrollTop !== unitsScrollRef.current.scrollTop) {
      unitsScrollRef.current.scrollTop = e.currentTarget.scrollTop;
    }
    // Synchronize horizontal scroll with day header
    if (dayHeaderScrollRef.current && e.currentTarget.scrollLeft !== dayHeaderScrollRef.current.scrollLeft) {
      dayHeaderScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleDayHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Synchronize horizontal scroll with calendar grid
    if (calendarScrollRef.current && e.currentTarget.scrollLeft !== calendarScrollRef.current.scrollLeft) {
      calendarScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  // Load user preference for day numbers on component mount and auto-scroll to today
  useEffect(() => {
    // We are now using localStorage for day numbers preference
  }, []); // Only run once on component mount

  // Handler for day numbers checkbox change
  const handleDayNumbersChange = async (checked: boolean) => {
    setShowDayNumbers(checked);
    // Update day display mode based on the checked state
    if (checked) {
      setDayDisplayMode('day_numbers');
    } else {
      setDayDisplayMode('none');
    }
    try {
      await userPreferencesApi.set('show_day_numbers', checked);
    } catch (error) {
      console.error('Failed to save day numbers preference:', error);
    }
  };

  // Change rate functionality
  const handleRateCellClick = (date: Date, unitId: number, propertyId: number) => {
    if (calendarMode === 'change_rate') {
      // Check if this cell is already selected (for multi-cell editing)
      const isSelected = selectedCells.some(cell => 
        isSameDay(cell.date, date) && 
        cell.unitId === unitId && 
        cell.propertyId === propertyId
      );
      
      // Only allow editing if the cell is selected or if there are no other selections
      if (isSelected || selectedCells.length === 0) {
        setSelectedRateCell({ date, unitId, propertyId });
        setEditingRate('');
      }
    } else if (onDayCellClick) {
      onDayCellClick(date, propertyId, unitId);
    }
  };

  const handleRateSave = async () => {
    if (!selectedRateCell || !editingRate) return;

    try {
      const rate = parseFloat(editingRate);
      if (isNaN(rate) || rate < 0) {
        console.error('Invalid rate value');
        return;
      }

      // Check if there are multiple selected cells
      const cellsToUpdate = selectedCells.length > 1 ? selectedCells : [selectedRateCell];
      
      // Prepare batch update data
      const batchUpdates = cellsToUpdate.map(cell => ({
        unit_id: cell.unitId,
        date: format(cell.date, 'yyyy-MM-dd'),
        rate: rate
      }));

      // Send single batch request to update all cells
      await dailyRatesApi.batchUpdateRates({
        updates: batchUpdates
      });

      // Update the cache for all cells
      for (const cell of cellsToUpdate) {
        updateRateInCache(cell.date, cell.unitId, rate);
      }

      setSelectedRateCell(null);
      setEditingRate('');
      // Clear selection after successful update
      setSelectedCells([]);
      
      // Refresh the calendar
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to update rate:', error);
    }
  };

  const handleRateCancel = () => {
    setSelectedRateCell(null);
    setEditingRate('');
    // Clear selection when canceling editing
    setSelectedCells([]);
  };

  const handleCellSelectionStart = (date: Date, unitId: number, propertyId: number) => {
    if (calendarMode === 'change_rate') {
      setIsSelectingCells(true);
      setSelectionStart({ date, unitId, propertyId });
      // Don't clear existing selection on mouse down - let the click handler manage single clicks
    }
  };

  const handleCellSelectionMove = (date: Date, unitId: number, propertyId: number) => {
    if (calendarMode === 'change_rate' && isSelectingCells && selectionStart) {
      setHasDragged(true);
      // Calculate the range of selected cells
      const startDate = selectionStart.date;
      const endDate = date;
      
      const selectedRange = [];
      let currentDate = new Date(Math.min(startDate.getTime(), endDate.getTime()));
      const maxDate = new Date(Math.max(startDate.getTime(), endDate.getTime()));
      
      while (currentDate <= maxDate) {
        selectedRange.push({
          date: new Date(currentDate),
          unitId,
          propertyId
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setSelectedCells(selectedRange);
    }
  };

  const handleCellSelectionEnd = () => {
    setIsSelectingCells(false);
    setSelectionStart(null);
  };

  const isCellSelected = (date: Date, unitId: number, propertyId: number) => {
    const isSelected = selectedCells.some(cell => 
      isSameDay(cell.date, date) && 
      cell.unitId === unitId && 
      cell.propertyId === propertyId
    );
    return isSelected;
  };

  const isCellEditing = (date: Date, unitId: number, propertyId: number) => {
    return selectedRateCell && 
           isSameDay(selectedRateCell.date, date) && 
           selectedRateCell.unitId === unitId && 
           selectedRateCell.propertyId === propertyId;
  };

  // State for caching daily rates
  const [dailyRatesCache, setDailyRatesCache] = React.useState<{[key: string]: number}>({});

  // Function to get rate for a specific day and unit
  // Helper function to format rate values cleanly
  const formatRateValue = (value: number) => {
    // Convert to string and remove trailing zeros after decimal point
    const formatted = value.toString();
    
    // If it's an integer, return as is
    if (Number.isInteger(value)) {
      return formatted;
    }
    
    // If it has decimal places, remove trailing zeros
    if (formatted.includes('.')) {
      return formatted.replace(/\.?0+$/, '');
    }
    
    return formatted;
  };

  const getRateForDay = (date: Date, unitId: number) => {
    const cacheKey = `${unitId}-${format(date, 'yyyy-MM-dd')}`;
    
    // Check if we have the rate in cache
    if (dailyRatesCache[cacheKey] !== undefined) {
      const rate = dailyRatesCache[cacheKey];
      return formatRateValue(rate);
    }
    
    // Fallback to unit's default rent amount
    const unit = units.find(u => u.id === unitId);
    if (!unit) return '0';
    
    const rate = unit.rent_amount || 0;
    return formatRateValue(rate);
  };

  const getTooltipText = (date: Date, unitId: number, propertyId: number) => {
    const rate = getRateForDay(date, unitId);
    const dateStr = format(date, 'EEEE, MMMM d, yyyy');
    
    return `${dateStr}\nRate: ${rate}`;
  };

  // Function to update rate in cache
  const updateRateInCache = (date: Date, unitId: number, rate: number) => {
    const cacheKey = `${unitId}-${format(date, 'yyyy-MM-dd')}`;
    setDailyRatesCache(prev => ({
      ...prev,
      [cacheKey]: rate
    }));
  };

  // Function to load rates for the current month (optimized)
  const loadRatesForCurrentMonth = async () => {
    try {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const monthKey = format(monthStart, 'yyyy-MM');

      // Helper: check if cache contains any key for a specific unit in this month
      const hasCacheForUnitMonth = (unitId: number): boolean => {
        const prefix = `${unitId}-${monthKey}-`;
        // Keys are stored as `${unitId}-yyyy-MM-dd`
        return Object.keys(dailyRatesCache).some(k => k.startsWith(prefix));
      };

      // Determine which units need fetching for this month
      const unitIds = (units || []).map(u => u.id);
      const unitsToFetch = unitIds.filter(id => !hasCacheForUnitMonth(id));

      if (unitsToFetch.length === 0) {
        // Everything already cached for this month
        return;
      }

      const startDateStr = format(monthStart, 'yyyy-MM-dd');
      const endDateStr = format(monthEnd, 'yyyy-MM-dd');

      // Try optimized multi-unit endpoint first
      try {
        const multi = await dailyRatesApi.getMultipleUnitRates(unitsToFetch, startDateStr, endDateStr);
        if (multi && Array.isArray(multi.units)) {
          multi.units.forEach(u => {
            Object.entries(u.rates).forEach(([dateStr, rate]) => {
              const date = parseISO(dateStr);
              updateRateInCache(date, u.unit_id, rate as number);
            });
          });
          return;
        }
      } catch (multiErr) {
        // Fall back to per-unit parallel requests
        console.warn('Multi-unit rates endpoint failed, falling back to per-unit requests');
      }

      // Fallback: fetch per unit in parallel
      await Promise.all(
        unitsToFetch.map(async (unitId) => {
          const response = await dailyRatesApi.getRates(unitId, startDateStr, endDateStr);
          Object.entries(response.rates).forEach(([dateStr, rate]) => {
            const date = parseISO(dateStr);
            updateRateInCache(date, unitId, rate as number);
          });
        })
      );
    } catch (error) {
      console.error('Failed to load rates for current month:', error);
    }
  };

  // Load rates when current date changes (for tooltips and rates display)
  React.useEffect(() => {
    // Prefetch immediately; function skips already-cached units
    loadRatesForCurrentMonth();
  }, [format(currentDate, 'yyyy-MM'), units.map(u => u.id).join(',')]);

  // Clear selected cells when properties change (to prevent cross-property selections)
  React.useEffect(() => {
    console.log('Properties changed, clearing selections. Properties:', properties.map(p => p.id));
    setSelectedCells([]);
  }, [properties.map(p => p.id).join(',')]); // Only trigger when property IDs actually change

  // Clear selected cells when calendar mode changes
  React.useEffect(() => {
    console.log('Calendar mode changed, clearing selections. Mode:', calendarMode);
    setSelectedCells([]);
  }, [calendarMode]);

  // Clear selected cells when current date changes (to handle property navigation)
  React.useEffect(() => {
    console.log('Current date changed, clearing selections. Date:', currentDate);
    setSelectedCells([]);
  }, [format(currentDate, 'yyyy-MM')]); // Only trigger when month/year changes, not every day

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isChangeRateMode = (calendarMode as string) === 'change_rate';

  // Fixed cell width for consistency across all devices
  const cellWidth = 40;

  // Effect to handle scrolling to today after month change
  useEffect(() => {
    if (shouldScrollToToday && calendarDays.length > 0) {
      const today = new Date();
      const todayIndex = calendarDays.findIndex(day => isSameDay(day, today));
      
      if (todayIndex !== -1) {
        // Calculate scroll position based on responsive cell width
        const scrollPosition = todayIndex * cellWidth - 200; // Center today with some offset
        
        // Scroll the day header to today
        if (dayHeaderScrollRef.current) {
          dayHeaderScrollRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
        
        // Scroll the calendar grid to today
        if (calendarScrollRef.current) {
          calendarScrollRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
      }
      
      setShouldScrollToToday(false); // Reset the flag
    }
  }, [shouldScrollToToday, calendarDays, cellWidth]);

  return (
    <Box>
      {/* Sections Above Calendar */}
      {showSectionsAboveCalendar && (
        <>
        </>
      )}

            {/* Container for Payment Status and Calendar */}
      <Box sx={{ 
        position:showSectionsAboveCalendar ? 'sticky' : 'fixed',
        top: showSectionsAboveCalendar ? 70 : 80, // Different top values based on sections visibility
        width: sidebarCollapsed ? '99%' :showSectionsAboveCalendar ?'100%': `calc(99% - ${drawerWidth}px)`,
        left: sidebarCollapsed ? showSectionsAboveCalendar ? 0 : '0.5%': showSectionsAboveCalendar ? `${drawerWidth}px` :`calc(${drawerWidth}px + 0.5%)`,
        zIndex: 1000,
        paddingTop: 0.5,
        paddingLeft: 0.2,
        paddingRight: 0.2,
        bgcolor:  'RGB(220,220,255)'
      }}>
        {/* Calendar Toolbar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          gap: 2, 
          mb: 1, 
          p: 2, 
          bgcolor: 'white', 
          borderRadius: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={isRTL ? goToNextMonth : goToPreviousMonth}>
              {isRTL ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
              {format(currentDate, 'MMMM yyyy')}
            </Typography>
            <IconButton onClick={isRTL ? goToPreviousMonth : goToNextMonth}>
              {isRTL ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Filter Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={toggleFilter}
              startIcon={<FilterList />}
              sx={{ 
                borderColor: showFilterBar ? 'primary.main' : 'grey.400', 
                color: showFilterBar ? 'primary.main' : 'grey.600',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                }
              }}
            >
              Filter
            </Button>
            
            {/* Today Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={scrollToToday}
              sx={{ 
                borderColor: 'rgb(20, 200, 65)', 
                color: 'rgb(20, 200, 65)',
                '&:hover': {
                  borderColor: 'rgb(20, 200, 65)',
                  backgroundColor: 'rgba(20, 200, 65, 0.1)',
                }
              }}
            >
              Today
            </Button>
            
            {/* Calendar Mode Selector */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('calendar_mode')}</InputLabel>
              <Select
                value={calendarMode}
                onChange={async (e) => {
                  const newMode = e.target.value as 'booking' | 'change_rate';
                  setCalendarMode(newMode);
                  // Clear selections when switching modes
                  setSelectedCells([]);
                  
                  // Automatically set Day Display to "Rates" when switching to Change Rate mode
                  if (newMode === 'change_rate') {
                    setDayDisplayMode('rates');
                    setShowDayNumbers(true);
                    // Load rates for the current month
                    await loadRatesForCurrentMonth();
                  }
                }}
                label={t('calendar_mode')}
              >
                <MenuItem value="booking">{t('booking')}</MenuItem>
                <MenuItem value="change_rate">{t('change_rate')}</MenuItem>
              </Select>
            </FormControl>
            
            {/* Day Display Selector */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('day_display')}</InputLabel>
              <Select
                value={dayDisplayMode}
                onChange={async (e) => {
                  const value = e.target.value as 'none' | 'day_numbers' | 'rates';
                  setDayDisplayMode(value);
                  if (value === 'none') {
                    setShowDayNumbers(false);
                    // Automatically switch to Booking mode for None display
                    setCalendarMode('booking');
                    setSelectedCells([]);
                  } else if (value === 'day_numbers') {
                    setShowDayNumbers(true);
                    // Automatically switch to Booking mode for Day Numbers display
                    setCalendarMode('booking');
                    setSelectedCells([]);
                  } else if (value === 'rates') {
                    setShowDayNumbers(true); // Show something in the cell
                    // Clear selections when switching to rates mode
                    setSelectedCells([]);
                    // Load rates for the current month when switching to rates mode
                    await loadRatesForCurrentMonth();
                  }
                }}
                label={t('day_display')}
              >
                <MenuItem value="none">{t('none')}</MenuItem>
                <MenuItem value="day_numbers">{t('day_numbers')}</MenuItem>
                <MenuItem value="rates">{t('rates')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Filter Bar */}
        {showFilterBar && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3, 
            mb: 1, 
            p: 2, 
            bgcolor: 'white', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'black'
          }}>
            <Typography variant="body2" fontWeight="medium" color="primary.main">
              Filter Units:
            </Typography>
            
            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: 'grey.600' }}>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'reserved' | 'non_reserved')}
                label="Status"
                sx={{
                  '& .MuiSelect-select': { color: 'black' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.400' }
                }}
              >
                <MenuItem value="all">All Units</MenuItem>
                <MenuItem value="reserved">Reserved</MenuItem>
                <MenuItem value="non_reserved">Non-reserved</MenuItem>
              </Select>
            </FormControl>
            
            {/* Date Navigation Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={goToPreviousDay}
                sx={{ color: 'black', p: 0.5 }}
              >
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  &lt;
                </Typography>
              </IconButton>
              
              <Button
                variant="text"
                size="small"
                onClick={goToToday}
                sx={{ 
                  color: 'primary.main', 
                  textTransform: 'none',
                  fontWeight: 'medium',
                  p: 0.5,
                  minWidth: 'auto'
                }}
              >
                Today
              </Button>
              
              <IconButton
                size="small"
                onClick={goToNextDay}
                sx={{ color: 'black', p: 0.5 }}
              >
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  &gt;
                </Typography>
              </IconButton>
            </Box>
            
            {/* Start Date */}
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              InputLabelProps={{ 
                shrink: true,
                sx: { color: 'grey.600' }
              }}
              sx={{ 
                minWidth: 150,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'grey.400' },
                  '& input': { color: 'black' }
                }
              }}
            />
            
            {/* End Date */}
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              InputLabelProps={{ 
                shrink: true,
                sx: { color: 'grey.600' }
              }}
              sx={{ 
                minWidth: 150,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'grey.400' },
                  '& input': { color: 'black' }
                }
              }}
            />
          </Box>
        )}

        {/* Color Legend */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 3, 
          mb: 2, 
          p: 2, 
          bgcolor: 'grey.50', 
          borderRadius: 1,
          height: 56, // Fixed height to prevent any layout shifts
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="body2" fontWeight="medium">
            {t('payment_status')}:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#616161', borderRadius: 0.5 }} />
            <Typography variant="body2">{t('pending')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: 0.5 }} />
            <Typography variant="body2">{t('not_paid')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: 0.5 }} />
            <Typography variant="body2">{t('partially_paid')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 0.5 }} />
            <Typography variant="body2">{t('fully_paid')}</Typography>
          </Box>
          
          {/* Hovered Cell Information - Always shows something to maintain layout */}
          <Box sx={{ 
            ml: 2, // Small margin from the last payment status item
            width: 250, // Fixed width to prevent layout shifts
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center'
          }}>
            {hoveredCellInfo.date ? (
              <Box sx={{ 
                p: 1, 
                bgcolor: 'grey.700', 
                borderRadius: 1,
                color: 'white',
                fontWeight: 'bold'
              }}>
                <Typography variant="body2">
                  {getHoveredCellDisplayText()}
                </Typography>
              </Box>
            ) : (
              // Invisible placeholder to maintain layout when no hover info
              <Box sx={{ 
                p: 1, 
                width: 1, // Minimal width
                height: 1, // Minimal height
                visibility: 'hidden' // Hidden but takes up space
              }}>
                <Typography variant="body2">
                  &nbsp;
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Calendar Grid */}
        <Paper elevation={2} sx={{ overflow: 'hidden', position: 'relative' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {/* Units Column */}
          <Box sx={{ width: unitsColumnWidth, borderRight: 1, borderColor: 'divider', bgcolor: 'grey.50', position: 'relative' }}>
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider', 
              bgcolor: 'grey.100',
              height: 80,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('units')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {units.length} {units.length === 1 ? 'unit' : 'units'}
              </Typography>
            </Box>
            
            {/* Units List */}
            <Box 
              ref={unitsScrollRef}
              onScroll={handleUnitsScroll}
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider', 
                overflowY: 'auto', 
                ...(showSectionsAboveCalendar
                  ? {
                      maxHeight: {
                        xs: 'calc(100vh - 300px)',
                        sm: 'calc(100vh - 250px)',
                        md: 'calc(100vh - 200px)',
                        lg: 'calc(100vh - 180px)',
                        xl: 'calc(100vh - 160px)',
                      },
                    }
                  : {
                      height: 'calc(100vh - 320px)',
                    }),
              }}
            >
              {Object.values(groupedUnits).map(({ property, units: propertyUnits }) => {
                // Pre-compute colors to ensure they update when state changes
                const propertyBgColor = getPropertyColor(property.id);
                const propertyTextColor = getPropertyTextColor(property.id);
                const unitBgColor = getUnitColor(property.units?.[0]?.id || 0, property.id);
                
                // Get all units for this property (not just the hierarchical result)
                const allPropertyUnits = units.filter(unit => unit.property_id === property.id);
                
                return (
                <Box key={`${property.id}-${colorUpdateTrigger}`}>
                  {/* Property Header */}
                  <Box sx={{ 
                    p: 1, 
                    bgcolor: customPropertyColors[property.id] ? propertyBgColor : alpha(propertyBgColor, 0.2),
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: customPropertyColors[property.id] 
                        ? alpha(propertyBgColor, 0.8) 
                        : alpha(propertyBgColor, 0.3),
                    }
                  }}
                  onClick={(e) => handlePropertyColorClick(e, property.id)}
                  title={`Click to edit ${property.name} colors (background & text)`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Palette sx={{ 
                        fontSize: 14, 
                        mr: 0.5, 
                        color: propertyTextColor || 'textSecondary' 
                      }} />
                      <Typography 
                        variant="caption" 
                        fontWeight="bold" 
                        sx={{ color: propertyTextColor || 'textSecondary' }}
                      >
                        {property.type === 'house' ? 'Single Unit' : property.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* Expand/Collapse All buttons for regular properties */}
                      {property.type !== 'house' && (
                        <>
                          <Tooltip title="Expand All">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                expandAllUnits(property.id);
                              }}
                              sx={{ 
                                p: 0.25,
                                mr: 0.5,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                              }}
                            >
                              <UnfoldMore sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Collapse All">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                collapseAllUnits(property.id);
                              }}
                              sx={{ 
                                p: 0.25,
                                mr: 0.5,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                              }}
                            >
                              <UnfoldLess sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => handlePropertyMenuClick(e, property)}
                        sx={{ 
                          ml: 0.5, 
                          p: 0.25,
                          position: 'relative',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                        }}
                      >
                        <MoreVert sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Units or Single Unit Property */}
                  {property.type === 'house' ? (
                    // For "Single Unit" properties, show a single property row
                    <Box
                      key={`property-${property.id}`}
                      sx={{
                        p: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: getUnitColor(property.id, property.id),
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium" 
                          sx={{ color: propertyTextColor || 'inherit' }}
                        >
                          {property.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleSingleUnitMenuClick(e, property)}
                        sx={{ 
                          ml: 0.5, 
                          p: 0.25,
                          position: 'relative',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                        }}
                      >
                        <MoreVert sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    // For regular properties, show unit rows with hierarchy
                    propertyUnits.map(unit => (
                      <Box
                        key={unit.id}
                        sx={{
                          p: 1.5,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: getUnitBackgroundColor(unit, property.id),
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          ml: getUnitIndentation(unit) / 8, // Convert px to spacing units
                        }}>
                          {/* Expand/Collapse button for units with children */}
                          {hasChildren(unit, allPropertyUnits) && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleUnitExpansion(unit.id);
                                }}
                                sx={{ 
                                  p: 0.25,
                                  mr: 0.5,
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                                }}
                              >
                                {expandedUnits.has(unit.id) ? (
                                  <ExpandLess sx={{ fontSize: 16 }} />
                                ) : (
                                  <ExpandMore sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                              <Chip
                                label={allPropertyUnits.filter(u => u.parent_unit_id === unit.id).length}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: 10,
                                  mr: 0.5,
                                  bgcolor: getUnitBorderColor(unit),
                                  color: 'white',
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            </Box>
                          )}
                          
                          {/* Hierarchy indicator for sub-units */}
                          {unit.level > 0 && (
                            <Box sx={{ 
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              mr: 1,
                              ml: hasChildren(unit, allPropertyUnits) ? 0 : 4,
                            }}>
                              {/* Vertical line connecting to parent */}
                              <Box sx={{ 
                                width: 1, 
                                height: 20, 
                                bgcolor: getUnitBorderColor(unit), 
                                opacity: 0.6,
                                mr: 0.5,
                              }} />
                              {/* Horizontal line to unit */}
                              <Box sx={{ 
                                width: 8, 
                                height: 1, 
                                bgcolor: getUnitBorderColor(unit), 
                                opacity: 0.6,
                                mr: 0.5,
                              }} />
                              {/* Unit level indicator */}
                              <Box sx={{ 
                                width: 6, 
                                height: 6, 
                                bgcolor: getUnitBorderColor(unit), 
                                opacity: 0.8,
                                borderRadius: '50%',
                                mr: 0.5,
                              }} />
                            </Box>
                          )}
                          
                          {/* Add spacing for units without children and no hierarchy indicator */}
                          {!hasChildren(unit, allPropertyUnits) && unit.level === 0 && (
                            <Box sx={{ width: 32, height: 1 }} /> // Spacer to align with other units
                          )}
                          
                          <Typography 
                            variant="body2" 
                            fontWeight="medium" 
                            sx={{ color: propertyTextColor || 'inherit' }}
                          >
                            {unit.unit_number}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              ml: 1, 
                              opacity: 0.8,
                              color: propertyTextColor || 'textSecondary',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            {unit.level > 0 ? getUnitHierarchyName(unit) : unit.name}
                            {/* Cleaning Status Indicator */}
                            {unit.cleaning_status && (
                              <Tooltip 
                                key={`cleaning-${unit.id}`}
                                title={
                                  unit.cleaning_status === 'cleaned' 
                                    ? 'Unit is cleaned' 
                                    : 'Unit needs cleaning'
                                }
                                disableHoverListener={isDragging}
                                disableFocusListener={isDragging}
                                disableTouchListener={isDragging}
                              >
                                {unit.cleaning_status === 'cleaned' ? (
                                  <CheckCircle 
                                    sx={{ 
                                      fontSize: 14, 
                                      color: 'success.main',
                                      opacity: 0.8
                                    }} 
                                  />
                                ) : (
                                  <CleaningServices 
                                    sx={{ 
                                      fontSize: 14, 
                                      color: 'warning.main',
                                      opacity: 0.8
                                    }} 
                                  />
                                )}
                              </Tooltip>
                            )}
                            {/* Check-in Status Indicator */}
                            {getUnitCheckinIndicator(unit)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleUnitMenuClick(e, unit)}
                          sx={{ 
                            ml: 0.5, 
                            p: 0.25,
                            position: 'relative',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                          }}
                        >
                          <MoreVert sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))
                  )}
                </Box>
                );
              })}
            </Box>
            
            {/* Resize Handle */}
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 4,
                height: '100%',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                zIndex: 10,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  opacity: 0.5,
                },
                '&:active': {
                  backgroundColor: 'primary.main',
                  opacity: 0.8,
                },
              }}
            />
          </Box>

          {/* Calendar Days */}
          <Box sx={{ flex: 1, overflowX: 'auto', position: 'relative' }}>
            {/* Fixed Days Header */}
            <Box 
              ref={dayHeaderScrollRef}
              onScroll={handleDayHeaderScroll}
              sx={{ 
                display: 'flex', 
                borderBottom: 1, 
                borderColor: 'divider', 
                bgcolor: 'grey.100', 
                height: 80,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                overflowX: 'auto',
              }}>
              {calendarDays.map(day => (
                <Box
                  key={day.toISOString()}
                  sx={{
                    width: cellWidth,
                    minWidth: cellWidth,
                    maxWidth: cellWidth,
                    p: 1,
                    textAlign: 'center',
                    borderRight: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    {format(day, 'EEE')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {format(day, 'd')}
                  </Typography>
                  {/* Today indicator - vertical line in the middle */}
                  {isSameDay(day, new Date()) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        width: 2,
                        height: '100%',
                        backgroundColor: 'rgb(20, 200, 65)',
                        transform: 'translateX(-50%)',
                        zIndex: 5,
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>

            {/* Bookings Grid */}
            <Box 
              ref={calendarScrollRef}
              onScroll={handleCalendarScroll}
              sx={{ 
                position: 'relative', 
                borderBottom: 1, 
                borderColor: 'divider', 
                overflowY: 'auto',
                overflowX: 'auto',
                ...(showSectionsAboveCalendar
                  ? {
                      maxHeight: {
                        xs: 'calc(100vh - 300px)',
                        sm: 'calc(100vh - 250px)',
                        md: 'calc(100vh - 200px)',
                        lg: 'calc(100vh - 180px)',
                        xl: 'calc(100vh - 160px)',
                      },
                    }
                  : {
                      height: 'calc(100vh - 320px)',
                    }),
              }}
            >
              {Object.values(groupedUnits).map(({ property, units: propertyUnits }) => {
                // Pre-compute property color for calendar grid
                const propertyBgColor = getPropertyColor(property.id);
                
                return (
                <Box key={`calendar-${property.id}-${colorUpdateTrigger}`}>
                  {/* Property Header Row */}
                  <Box sx={{ 
                    display: 'flex', 
                    height: 32,
                  }}>
                    {calendarDays.map(day => (
                      <Box
                        key={day.toISOString()}
                        sx={{
                          width: cellWidth,
                          minWidth: cellWidth,
                          maxWidth: cellWidth,
                          height: '100%',
                          borderRight: 1,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: customPropertyColors[property.id] 
                            ? alpha(propertyBgColor, 0.2) 
                            : alpha(propertyBgColor, 0.1),
                          position: 'relative',
                          flexShrink: 0,
                        }}
                      >
                        {/* Today indicator for property header row */}
                        {isSameDay(day, new Date()) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '50%',
                              top: 0,
                              bottom: 0,
                              width: '2px',
                              backgroundColor: 'rgb(20, 200, 65)',
                              transform: 'translateX(-50%)',
                              zIndex: 5,
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                  
                  {/* Units Rows or Single Unit Property Row */}
                  {property.type === 'house' ? (
                    // For "Single Unit" properties, show property row directly
                    <Box
                      key={`property-${property.id}`}
                      sx={{
                        display: 'flex',
                        height: 36,
                        position: 'relative',
                      }}
                    >
                      {calendarDays.map(day => {
                        // For single unit properties, check bookings at property level
                        const booking = calendarBookings.find(booking => {
                          const startDate = parseISO(booking.start_date);
                          const endDate = parseISO(booking.end_date);
                          const adjustedEndDate = new Date(endDate);
                          adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
                          return (
                            booking.property_id === property.id && 
                            day >= startDate && 
                            day <= adjustedEndDate
                          );
                        });
                        
                        return (
                          <Tooltip
                            title={!booking && calendarMode === 'booking' ? getTooltipText(day, property.id, property.id) : ''}
                            arrow
                            placement="top"
                            enterDelay={500}
                            leaveDelay={0}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  lineHeight: 1.4,
                                }
                              }
                            }}
                          >
                            <Box
                              key={day.toISOString()}
                              onDragOver={(e) => handleDragOver(e, day, property.id, property.id)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, day, property.id, property.id)}
                            sx={{
                              width: cellWidth,
                              minWidth: cellWidth,
                              maxWidth: cellWidth,
                              height: '100%',
                              borderRight: 1,
                              borderBottom: 1,
                              borderColor: 'divider',
                              position: 'relative',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              bgcolor: (() => {
                                if (isDragOverCurrentCell(day, property.id, property.id)) {
                                  return 'primary.light';
                                }
                                if (calendarMode === 'change_rate' && isCellSelected(day, property.id, property.id)) {
                                  return 'primary.main';
                                }
                                if (calendarMode === 'change_rate' && isCellEditing(day, property.id, property.id)) {
                                  return 'warning.200';
                                }
                                return booking ? 'transparent' : 'transparent';
                              })(),
                              '&:hover': {
                                bgcolor: (() => {
                                  if (booking) {
                                    return 'transparent';
                                  }
                                  if (calendarMode === 'change_rate' && isCellSelected(day, property.id, property.id)) {
                                    return 'primary.dark'; // Darker blue for hover on selected cell
                                  }
                                  if (calendarMode === 'change_rate' && isCellEditing(day, property.id, property.id)) {
                                    return 'warning.300'; // Darker yellow for hover on editing cell
                                  }
                                  return 'action.hover'; // Default hover for unselected cells
                                })(),
                              },
                            }}
                            onClick={() => {
                              // Don't handle click if there was a drag operation
                              if (hasDragged) {
                                setHasDragged(false);
                                return;
                              }
                              
                              // Cancel editing if clicking on a different cell while editing
                              if (selectedRateCell && !isCellEditing(day, property.id, property.id)) {
                                handleRateCancel();
                              }
                              
                              if (booking) {
                                handleBookingClick(booking);
                              } else if (calendarMode === 'change_rate') {
                                // Mark cell as selected in Change Rate mode
                                const isSelected = selectedCells.some(cell => 
                                  isSameDay(cell.date, day) && 
                                  cell.unitId === property.id && 
                                  cell.propertyId === property.id
                                );
                                
                                if (isSelected) {
                                  // Remove from selection
                                  setSelectedCells(prev => prev.filter(cell => 
                                    !(isSameDay(cell.date, day) && 
                                      cell.unitId === property.id && 
                                      cell.propertyId === property.id)
                                  ));
                                } else {
                                  // Add to selection
                                  setSelectedCells(prev => [...prev, { date: day, unitId: property.id, propertyId: property.id }]);
                                }
                              } else if (calendarMode === 'booking') {
                                // Show create new booking dialog in Booking mode
                                if (onDayCellClick) {
                                  onDayCellClick(day, property.id, property.id);
                                }
                              }
                            }}
                            onDoubleClick={() => {
                              if (!booking && calendarMode === 'change_rate') {
                                handleRateCellClick(day, property.id, property.id);
                              }
                            }}
                            // Mouse events removed to prevent interference with click selection
                          >
                            {/* Day number or rate for empty cells */}
                            {!booking && !isCellEditing(day, property.id, property.id) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: (() => {
                                    if ((calendarMode as string) === 'change_rate' && isCellSelected(day, property.id, property.id)) {
                                      return 'warning.main';
                                    }
                                    return dayDisplayMode === 'rates' ? 'blue' : 'green';
                                  })(),
                                  opacity: isChangeRateMode ? 0.9 : ((calendarMode as string) === 'booking' ? 0.3 : 1),
                                  fontSize: '0.7rem',
                                  fontWeight: 'normal',
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  pointerEvents: 'auto',
                                  textAlign: 'center',
                                }}
                              >
                                {dayDisplayMode === 'rates' ? 
                                  getRateForDay(day, property.id) : 
                                  dayDisplayMode === 'day_numbers' ? day.getDate() : ''
                                }
                              </Typography>
                            )}
                                
                                {/* Rate editing input */}
                                {isCellEditing(day, property.id, property.id) && (
                                  <Box sx={{ 
                                    position: 'absolute', 
                                    top: '50%', 
                                    left: '50%', 
                                    transform: 'translate(-50%, -50%)',
                                    width: '80%',
                                    zIndex: 10
                                  }}>
                                    <TextField
                                      size="small"
                                      value={editingRate}
                                      onChange={(e) => setEditingRate(e.target.value)}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          handleRateSave();
                                        } else if (e.key === 'Escape') {
                                          handleRateCancel();
                                        }
                                      }}
                                      onBlur={handleRateSave}
                                      autoFocus
                                      placeholder={selectedCells.length > 1 ? `Update ${selectedCells.length} cells` : ''}
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          fontSize: '0.7rem',
                                          height: '24px',
                                        },
                                        '& .MuiInputBase-input': {
                                          padding: '4px 8px',
                                          textAlign: 'center',
                                          color: 'warning.main',
                                          fontWeight: 'bold',
                                        }
                                      }}
                                    />
                                  </Box>
                                )}
                                {/* Today indicator - vertical line in the middle */}
                                {isSameDay(day, new Date()) && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: '50%',
                                      width: 2,
                                      height: '100%',
                                      backgroundColor: 'rgb(20, 200, 65)',
                                      transform: 'translateX(-50%)',
                                      zIndex: 5,
                                    }}
                                  />
                                )}
                          </Box>
                          </Tooltip>
                        );
                      })}
                      
                      {/* Booking Bars Overlay for Single Unit Property */}
                      {calendarBookings
                        .filter(booking => booking.property_id === property.id)
                        .map(booking => {
                          const startDate = parseISO(booking.start_date);
                          const endDate = parseISO(booking.end_date);
                          // End date should be exclusive (booking ends the day before check-out)
                          const adjustedEndDate = new Date(endDate);
                          adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
                          
                          // Get the current month boundaries
                          const monthStart = startOfMonth(currentDate);
                          const monthEnd = endOfMonth(currentDate);
                          
                          // Determine the visible portion of the booking in this month
                          const visibleStartDate = startDate < monthStart ? monthStart : startDate;
                          const visibleEndDate = adjustedEndDate > monthEnd ? monthEnd : adjustedEndDate;
                          
                          // If the booking doesn't intersect with this month, don't render it
                          if (visibleStartDate > monthEnd || visibleEndDate < monthStart) {
                            return null;
                          }
                          
                          // Calculate position and width based on visible portion
                          const startDayIndex = calendarDays.findIndex(day => isSameDay(day, visibleStartDate));
                          const endDayIndex = calendarDays.findIndex(day => isSameDay(day, visibleEndDate));
                          
                          if (startDayIndex === -1 || endDayIndex === -1) return null;
                          
                          const widthInDays = endDayIndex - startDayIndex + 1;
                          
                          // Calculate visual styling based on whether this is the actual start/end of the booking
                          const isActualStart = isSameDay(visibleStartDate, startDate);
                          const isActualEnd = isSameDay(visibleEndDate, adjustedEndDate);
                          
                          // Determine border radius based on actual booking boundaries
                          let borderRadius = '4px'; // Default square corners
                          if (isActualStart && isActualEnd) {
                            borderRadius = '14px'; // Fully rounded if booking starts and ends in this month
                          } else if (isActualStart) {
                            // In RTL, start should be rounded on the right side
                            borderRadius = isRTL ? '4px 14px 14px 4px' : '14px 4px 4px 14px';
                          } else if (isActualEnd) {
                            // In RTL, end should be rounded on the left side
                            borderRadius = isRTL ? '14px 4px 4px 14px' : '4px 14px 14px 4px';
                          }
                          
                          return (
                              <Box
                                key={`booking-${booking.id}`}
                                draggable
                                onDragStart={(e) => { handleDragStart(e, booking)}}
                                onDragEnd={handleDragEnd}
                                onClick={() => handleBookingClick(booking)}
                                title={isDragging ? '' : `${booking.displayName} (${booking.propertyName} - ${booking.unitName})`}
                                sx={{
                                  position: 'absolute',
                                  top: 2,
                                  left: startDayIndex * cellWidth + 1,
                                  width: widthInDays * cellWidth - 2,
                                  height: 32,
                                  bgcolor: getBookingColor(booking),
                                  borderRadius,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: isDragging && draggedBooking?.id === booking.id ? 'grabbing' : 'grab',
                                  zIndex: isDragging && draggedBooking?.id === booking.id ? 2000 : 1,
                                  opacity: isDragging && draggedBooking?.id === booking.id ? 0.5 : 1,
                                  '&:hover': {
                                    opacity: isDragging && draggedBooking?.id === booking.id ? 0.5 : 0.8,
                                    transform: isDragging && draggedBooking?.id === booking.id ? 'none' : 'scale(1.02)',
                                  },
                                  transition: 'all 0.2s ease-in-out',
                                  userSelect: 'none',
                                }}
                              >
                                {getBookingIcon(booking) && (
                                  <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                                    {getBookingIcon(booking)}
                                  </Box>
                                )}
                                <Typography variant="caption" sx={{ 
                                  color: 'white', 
                                  fontWeight: 'bold',
                                  textShadow: '0 0 2px rgba(0,0,0,0.5)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}>
                                  #{booking.id} {booking.displayName}
                                </Typography>
                              </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    // For regular properties, show unit rows
                    propertyUnits.map(unit => {
                      // Get all bookings for this unit in the current month
                      const unitBookings = calendarBookings.filter(booking => booking.unit_id === unit.id);
                      
                      return (
                        <Box
                          key={unit.id}
                          sx={{
                            display: 'flex',
                            height: 36,
                            position: 'relative',
                          }}
                        >
                          {calendarDays.map(day => {
                            const booking = getBookingForUnitAndDateWithDescendants(unit.id, day);
                            
                            return (
                              <Box
                                  key={day.toISOString()}
                                  onDragOver={(e) => handleDragOver(e, day, unit.id, property.id)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, day, unit.id, property.id)}
                                  onMouseEnter={() => handleCellMouseEnter(day, unit.id, property.id)}
                                  onMouseLeave={handleCellMouseLeave}
                                sx={{
                                  width: cellWidth,
                                  minWidth: cellWidth,
                                  maxWidth: cellWidth,
                                  height: '100%',
                                  borderRight: 1,
                                  borderBottom: 1,
                                  borderColor: 'divider',
                                  position: 'relative',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexShrink: 0,
                                  bgcolor: (() => {
                                    if (isDragOverCurrentCell(day, unit.id, property.id)) {
                                      return 'primary.light';
                                    }
                                    if (calendarMode === 'change_rate' && isCellSelected(day, unit.id, property.id)) {
                                      return 'primary.main';
                                    }
                                    if (calendarMode === 'change_rate' && isCellEditing(day, unit.id, property.id)) {
                                      return 'warning.200';
                                    }
                                    return 'transparent';
                                  })(),
                                                                '&:hover': {
                                bgcolor: (() => {
                                  if (booking) {
                                    return 'transparent';
                                  }
                                  if (calendarMode === 'change_rate' && isCellSelected(day, unit.id, property.id)) {
                                    return 'primary.dark'; // Darker blue for hover on selected cell
                                  }
                                  if (calendarMode === 'change_rate' && isCellEditing(day, unit.id, property.id)) {
                                    return 'warning.300'; // Darker yellow for hover on editing cell
                                  }
                                  return 'action.hover'; // Default hover for unselected cells
                                })(),
                              },
                                }}
                                                            onClick={() => {
                              // Don't handle click if there was a drag operation
                              if (hasDragged) {
                                setHasDragged(false);
                                return;
                              }
                              
                              // Cancel editing if clicking on a different cell while editing
                              if (selectedRateCell && !isCellEditing(day, unit.id, property.id)) {
                                handleRateCancel();
                              }
                              
                              if (booking) {
                                handleBookingClick(booking);
                              } else if (calendarMode === 'change_rate') {
                                // Mark cell as selected in Change Rate mode
                                const isSelected = selectedCells.some(cell => 
                                  isSameDay(cell.date, day) && 
                                  cell.unitId === unit.id && 
                                  cell.propertyId === property.id
                                );
                                
                                if (isSelected) {
                                  // Remove from selection
                                  setSelectedCells(prev => prev.filter(cell => 
                                    !(isSameDay(cell.date, day) && 
                                      cell.unitId === unit.id && 
                                      cell.propertyId === property.id)
                                  ));
                                } else {
                                  // Add to selection
                                  setSelectedCells(prev => [...prev, { date: day, unitId: unit.id, propertyId: property.id }]);
                                }
                              } else if (calendarMode === 'booking') {
                                // Show create new booking dialog in Booking mode
                                if (onDayCellClick) {
                                  onDayCellClick(day, property.id, unit.id);
                                }
                              }
                            }}
                            onDoubleClick={() => {
                              if (!booking && calendarMode === 'change_rate') {
                                handleRateCellClick(day, unit.id, property.id);
                              }
                            }}
                                                            // Mouse events removed to prevent interference with click selection
                              >
                                {/* Today indicator */}
                                {isSameDay(day, new Date()) && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      left: '50%',
                                      top: 0,
                                      bottom: 0,
                                      width: '2px',
                                      backgroundColor: 'rgb(20, 200, 65)',
                                      transform: 'translateX(-50%)',
                                      zIndex: 5,
                                      pointerEvents: 'none',
                                    }}
                                  />
                                )}
                                
                                {/* Day number or rate for empty cells */}
                                {!booking && !isCellEditing(day, unit.id, property.id) && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: (() => {
                                        if ((calendarMode as string) === 'change_rate' && isCellSelected(day, unit.id, property.id)) {
                                          return 'white';
                                        }
                                        return dayDisplayMode === 'rates' ? 'blue' : 'green';
                                      })(),
                                      opacity: isChangeRateMode ? 0.9 : ((calendarMode as string) === 'booking' ? 0.3 : 1),
                                      fontSize: '0.7rem',
                                      fontWeight: 'normal',
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      pointerEvents: 'auto',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {dayDisplayMode === 'rates' ? 
                                      getRateForDay(day, unit.id) : 
                                      dayDisplayMode === 'day_numbers' ? day.getDate() : ''
                                    }
                                  </Typography>
                                )}
                                
                                {/* Rate editing input */}
                                {isCellEditing(day, unit.id, property.id) && (
                                  <Box sx={{ 
                                    position: 'absolute', 
                                    top: '50%', 
                                    left: '50%', 
                                    transform: 'translate(-50%, -50%)',
                                    width: '80%',
                                    zIndex: 10
                                  }}>
                                    <TextField
                                      size="small"
                                      value={editingRate}
                                      onChange={(e) => setEditingRate(e.target.value)}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          handleRateSave();
                                        } else if (e.key === 'Escape') {
                                          handleRateCancel();
                                        }
                                      }}
                                      onBlur={handleRateSave}
                                      autoFocus
                                      placeholder={selectedCells.length > 1 ? `Update ${selectedCells.length} cells` : ''}
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          fontSize: '0.7rem',
                                          height: '24px',
                                        },
                                        '& .MuiInputBase-input': {
                                          padding: '4px 8px',
                                          textAlign: 'center',
                                          color: 'warning.main',
                                          fontWeight: 'bold',
                                        }
                                      }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                          
                          {/* Booking Bars Overlay */}
                          {unitBookings.map(booking => {
                            const startDate = parseISO(booking.start_date);
                            const endDate = parseISO(booking.end_date);
                            // End date should be exclusive (booking ends the day before check-out)
                            const adjustedEndDate = new Date(endDate);
                            adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
                            
                            // Get the current month boundaries
                            const monthStart = startOfMonth(currentDate);
                            const monthEnd = endOfMonth(currentDate);
                            
                            // Determine the visible portion of the booking in this month
                            const visibleStartDate = startDate < monthStart ? monthStart : startDate;
                            const visibleEndDate = adjustedEndDate > monthEnd ? monthEnd : adjustedEndDate;
                            
                            // If the booking doesn't intersect with this month, don't render it
                            if (visibleStartDate > monthEnd || visibleEndDate < monthStart) {
                              return null;
                            }
                            
                            // Calculate position and width based on visible portion
                            const startDayIndex = calendarDays.findIndex(day => isSameDay(day, visibleStartDate));
                            const endDayIndex = calendarDays.findIndex(day => isSameDay(day, visibleEndDate));
                            
                            if (startDayIndex === -1 || endDayIndex === -1) return null; // Should not happen now
                            
                            const widthInDays = endDayIndex - startDayIndex + 1;
                            
                            // Calculate visual styling based on whether this is the actual start/end of the booking
                            const isActualStart = isSameDay(visibleStartDate, startDate);
                            const isActualEnd = isSameDay(visibleEndDate, adjustedEndDate);
                            
                            // Determine border radius based on actual booking boundaries
                            let borderRadius = '4px'; // Default square corners
                            if (isActualStart && isActualEnd) {
                              borderRadius = '14px'; // Fully rounded if booking starts and ends in this month
                            } else if (isActualStart) {
                              // In RTL, start should be rounded on the right side
                              borderRadius = isRTL ? '4px 14px 14px 4px' : '14px 4px 4px 14px';
                            } else if (isActualEnd) {
                              // In RTL, end should be rounded on the left side
                              borderRadius = isRTL ? '14px 4px 4px 14px' : '4px 14px 14px 4px';
                            }
                            
                            return (
                                <Box
                                  key={`${booking.id}`}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, booking)}
                                  onDragEnd={handleDragEnd}
                                  title={isDragging ? '' : `${booking.displayName} (${booking.propertyName} - ${booking.unitName})`}
                                  sx={{
                                    position: 'absolute',
                                    ...(isRTL 
                                      ? { 
                                          right: `${startDayIndex * cellWidth}px`,
                                          direction: 'rtl'
                                        } 
                                      : { left: `${startDayIndex * cellWidth}px` }
                                    ),
                                    width: `${widthInDays * cellWidth}px`,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    height: '90%',
                                    backgroundColor: booking.color,
                                    borderRadius: borderRadius,
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1,
                                    zIndex: isDragging && draggedBooking?.id === booking.id ? 2000 : 1000,
                                    cursor: isDragging && draggedBooking?.id === booking.id ? 'grabbing' : 'grab',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    border: '2px solid white',
                                    opacity: isDragging && draggedBooking?.id === booking.id ? 0.5 : 1,
                                    '&:hover': {
                                      transform: isDragging && draggedBooking?.id === booking.id ? 'translateY(-50%)' : 'translateY(-50%) scale(1.02)',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                    userSelect: 'none',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBookingClick(booking);
                                  }}
                                >
                                  {booking.icon && (
                                    <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                                      {booking.icon}
                                    </Box>
                                  )}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '11px',
                                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      flex: 1,
                                    }}
                                  >
                                    #{booking.id} {booking.displayName}
                                  </Typography>
                                </Box>
                            );
                          })}
                        </Box>
                      );
                    })
                  )}
                </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Paper>
        </Box>

      {/* Property Action Menu */}
      <Popover
        anchorEl={propertyMenuAnchor}
        open={Boolean(propertyMenuAnchor)}
        onClose={handleClosePropertyMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disablePortal={false}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              mt: 0.5,
              minWidth: 150,
              maxWidth: 200,
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: '50%',
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateX(-50%) translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuList dense>
          <MenuItem onClick={() => handleQuickDetails('property')}>
            <ListItemIcon>
              <Info fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleExpenses('property')}>
            <ListItemIcon>
              <AttachMoney fontSize="small" />
            </ListItemIcon>
            <ListItemText>Expenses</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handlePurchaseInvoice('property')}>
            <ListItemIcon>
              <Receipt fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('purchase_invoice')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleReport('property')}>
            <ListItemIcon>
              <Assessment fontSize="small" />
            </ListItemIcon>
            <ListItemText>Report</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>

      {/* Unit Action Menu */}
      <Popover
        anchorEl={unitMenuAnchor}
        open={Boolean(unitMenuAnchor)}
        onClose={handleCloseUnitMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disablePortal={false}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              mt: 0.5,
              minWidth: 150,
              maxWidth: 200,
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: '50%',
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateX(-50%) translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuList dense>
          <MenuItem onClick={() => handleQuickDetails('unit')}>
            <ListItemIcon>
              <Info fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCheckinCheckout}>
            <ListItemIcon>
              <Login fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('checkin_checkout')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleExpenses('unit')}>
            <ListItemIcon>
              <AttachMoney fontSize="small" />
            </ListItemIcon>
            <ListItemText>Expenses</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handlePurchaseInvoice('unit')}>
            <ListItemIcon>
              <Receipt fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('purchase_invoice')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCleaning}>
            <ListItemIcon>
              <CleaningServices fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('cleaning')}</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>

      {/* Color Edit Dialog */}
      <Dialog open={showColorEditDialog} onClose={handleCancelColorEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ColorLens />
          Edit Colors - {selectedPropertyForMenu?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Background Color */}
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Background Color</FormLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  type="color"
                  value={tempBackgroundColor || '#ffffff'}
                  onChange={(e) => setTempBackgroundColor(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  fullWidth
                  value={tempBackgroundColor || ''}
                  onChange={(e) => handleBackgroundColorChange(e.target.value)}
                  placeholder="#ffffff"
                  helperText="Enter hex color or use color picker"
                />
              </Box>
            </FormControl>

            {/* Text Color */}
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Text Color</FormLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  type="color"
                  value={tempTextColor || '#000000'}
                  onChange={(e) => setTempTextColor(e.target.value)}
                  sx={{ width: 80 }}
                />
                <TextField
                  fullWidth
                  value={tempTextColor || ''}
                  onChange={(e) => handleTextColorChange(e.target.value)}
                  placeholder="#000000"
                  helperText="Enter hex color or use color picker"
                />
              </Box>
            </FormControl>

            {/* Preview */}
            <Box sx={{ mt: 2 }}>
              <FormLabel sx={{ mb: 1 }}>Preview:</FormLabel>
              <Box
                sx={{
                  p: 2,
                  bgcolor: tempBackgroundColor || '#ffffff',
                  color: tempTextColor || '#000000',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" fontWeight="medium">
                  {selectedPropertyForMenu?.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelColorEdit}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveColors}
            disabled={!tempBackgroundColor || !tempTextColor || tempBackgroundColor.length < 7 || tempTextColor.length < 7}
          >
            Save Colors
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Picker Popover */}
      <Popover
        open={showColorPicker}
        anchorEl={colorPickerAnchor}
        onClose={handleCloseColorPicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Choose Background Color
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
            {colorPalette.map((color, index) => (
              <Box
                key={index}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: color,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: selectedPropertyForColor !== null && getPropertyColor(selectedPropertyForColor) === color 
                    ? '3px solid #000' 
                    : '1px solid #ccc',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.1s',
                  }
                }}
                onClick={() => handleColorChange(color)}
                title={`Color ${index + 1}`}
              />
            ))}
          </Box>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={handleCloseColorPicker}
          >
            Cancel
          </Button>
        </Box>
      </Popover>

      {/* Booking Details Dialog */}
      <Dialog open={showBookingDialog} onClose={handleCloseBookingDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedBooking?.icon}
          {t('booking_details')} #{selectedBooking?.booking_number || selectedBooking?.id}
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('guest_name')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedBooking.tenant_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('phone_number')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedBooking.tenant_phone || 'No phone provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('email')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedBooking.tenant_email || 'No email provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('property')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedBooking.propertyName} - {getUnitHierarchyNameById(selectedBooking.unit_id)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('check_in')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(parseISO(selectedBooking.start_date), 'PPP')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('check_out')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(parseISO(selectedBooking.end_date), 'PPP')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('status')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={t(selectedBooking.status)}
                    color={
                      selectedBooking.status === 'active' ? 'success' :
                      selectedBooking.status === 'pending' ? 'warning' :
                      selectedBooking.status === 'completed' ? 'info' : 'default'
                    }
                  />
                  {selectedBooking.status === 'pending' && selectedBooking.auto_cancel_at && (
                    <Typography variant="body2" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      fontWeight: 'medium',
                      color: (() => {
                        const now = new Date();
                        const cancelTime = new Date(selectedBooking.auto_cancel_at);
                        const timeDiff = cancelTime.getTime() - now.getTime();
                        return timeDiff <= 0 ? 'error.main' : 'warning.main';
                      })()
                    }}>
                      <AccessTime sx={{ fontSize: 16 }} />
                      {(() => {
                        const now = new Date();
                        const cancelTime = new Date(selectedBooking.auto_cancel_at);
                        const timeDiff = cancelTime.getTime() - now.getTime();
                        
                        if (timeDiff <= 0) {
                          return 'Auto-cancel expired';
                        }
                        
                        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
                        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                        
                        if (hours > 0) {
                          return `${hours}h ${minutes}m until auto-cancel`;
                        } else {
                          return `${minutes}m until auto-cancel`;
                        }
                      })()}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('rent_amount')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(selectedBooking, 'rent_amount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Deposit Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(selectedBooking, 'deposit_amount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Extra Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(selectedBooking, 'extra_amount')}
                </Typography>
              </Grid>
              {selectedBooking.extra_description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Extra Amount Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.extra_description}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Number of Nights
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {(() => {
                    const start = parseISO(selectedBooking.start_date);
                    const end = parseISO(selectedBooking.end_date);
                    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                    return nights;
                  })()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Rent per Night
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {(() => {
                    const start = parseISO(selectedBooking.start_date);
                    const end = parseISO(selectedBooking.end_date);
                    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                    const rentAmount = Number(selectedBooking.rent_amount || 0);
                    const rentPerNight = nights > 0 ? (rentAmount / nights) : 0;
                    
                    // Currency hierarchy: booking.currency > property.currency > default
                    let symbol = '$'; // Default fallback
                    
                    if ((selectedBooking as any).currency?.symbol) {
                      symbol = (selectedBooking as any).currency.symbol;
                    } else if ((selectedBooking as any).property?.currency?.symbol) {
                      symbol = (selectedBooking as any).property.currency.symbol;
                    } else {
                      symbol = getMainCurrencySymbol();
                    }
                    
                    return `${symbol}${rentPerNight.toFixed(2)}`;
                  })()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(selectedBooking, 'total_amount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Remaining Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatRemainingAmount(selectedBooking)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Payment Status
                </Typography>
                <Chip
                  label={selectedBooking.payment_status || 'none'}
                  color={
                    selectedBooking.payment_status === 'full' ? 'success' :
                    selectedBooking.payment_status === 'partial' ? 'warning' : 'default'
                  }
                />
              </Grid>
              {selectedBooking.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('notes')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.notes}
                  </Typography>
                </Grid>
              )}
              
              {/* Booking History Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assessment />
                  Booking History
                </Typography>
                
                {loadingHistory ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading history...
                  </Typography>
                ) : bookingHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No history available
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    {bookingHistory.map((history, index) => (
                      <Box
                        key={history.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          py: 1.5,
                          px: 2,
                          backgroundColor: index % 2 === 0 ? 'background.default' : 'background.paper',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 
                              history.action === 'created' ? 'success.main' :
                              history.action === 'updated' ? 'info.main' :
                              history.action === 'deleted' ? 'error.main' : 'grey.500',
                            mt: 0.75,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {history.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(parseISO(history.created_at), 'PPpp')} by {history.user?.name || 'Unknown User'}
                          </Typography>
                          {history.action === 'updated' && history.old_values && history.new_values && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Changes made:
                              </Typography>
                              {Object.keys(history.new_values).map((key) => {
                                const oldValue = history.old_values?.[key];
                                const newValue = history.new_values?.[key];
                                if (oldValue !== newValue) {
                                  return (
                                    <Typography key={key} variant="caption" display="block" sx={{ ml: 1 }}>
                                      • {getFieldDisplayName(key)}: {String(oldValue)} → {String(newValue)}
                                    </Typography>
                                  );
                                }
                                return null;
                              })}
                            </Box>
                          )}
                        </Box>
                        {/* Add edit button for payment entries */}
                        {history.description.toLowerCase().includes('payment') && (
                          <Box sx={{ ml: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditPaymentDialog(history)}
                              sx={{ 
                                opacity: 0.7, 
                                '&:hover': { opacity: 1 },
                                color: 'primary.main' 
                              }}
                              title="Edit Payment"
                            >
                              <Edit />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBookingDialog}>
            {t('close')}
          </Button>
          {selectedBooking && selectedBooking.status === 'pending' && (
            <Button
              startIcon={<CheckCircle />}
              onClick={async () => {
                try {
                  await bookingApi.confirm(selectedBooking.id);
                  // Update the selected booking status
                  setSelectedBooking(prev => prev ? { ...prev, status: 'active' } : null);
                  // Refresh the calendar
                  if (onRefresh) onRefresh();
                  queryClient.invalidateQueries({ queryKey: ['bookings'] });
                } catch (error) {
                  console.error('Failed to confirm booking:', error);
                }
              }}
              color="success"
              variant="contained"
            >
              Confirm Booking
            </Button>
          )}
          {selectedBooking && (
            <Button
              startIcon={<AttachMoney />}
              onClick={handleOpenPaymentDialog}
              color="primary"
            >
              Add Payment
            </Button>
          )}
          {selectedBooking && (
            <Button
              startIcon={<ConfirmationNumber />}
              onClick={() => {
                console.log('🎫 Generate ticket for booking:', selectedBooking);
                console.log('🎫 Current ticketDocuments:', ticketDocuments);
                console.log('🎫 Current ticketLanguage:', ticketLanguage);
                console.log('🎫 Current ticketDocumentContent:', ticketDocumentContent);
                setShowTicketDialog(true);
                
                // Always try to fetch documents when dialog opens
                if (!ticketDocuments || ticketDocuments.length === 0) {
                  console.log('🎫 No documents found, fetching...');
                  fetchTicketDocuments().then(() => {
                    fetchTicketDocumentContent(ticketLanguage);
                  });
                } else {
                  console.log('🎫 Documents available, fetching content...');
                  fetchTicketDocumentContent(ticketLanguage);
                }
              }}
              color="secondary"
            >
              Ticket
            </Button>
          )}
          {selectedBooking && onEditBooking && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {
                onEditBooking(selectedBooking);
                handleCloseBookingDialog();
              }}
            >
              {t('edit')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoney />
          Add Payment
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {(() => {
              const totalAmount = Number(selectedBooking?.total_amount || 0);
              const paidAmount = Number(selectedBooking?.paid_amount || 0);
              const remainingAmount = totalAmount - paidAmount;
              const symbol = (selectedBooking as any)?.currency?.symbol || getMainCurrencySymbol();
              const enteredAmount = Number(paymentAmount);
              const isOverpayment = !isNaN(enteredAmount) && enteredAmount > remainingAmount;
              
              return (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Remaining Amount: {symbol}{remainingAmount.toFixed(2)}
                  </Typography>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Payment Amount"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    error={isOverpayment}
                    inputProps={{
                      min: 0,
                      step: 0.01
                    }}
                    sx={{ mt: 2 }}
                  />
                  {isOverpayment && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      Payment amount cannot exceed the remaining amount of {symbol}{remainingAmount.toFixed(2)}
                    </Typography>
                  )}
                  {paymentAmount && !isNaN(enteredAmount) && enteredAmount > 0 && !isOverpayment && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      New total paid will be: {symbol}{(paidAmount + enteredAmount).toFixed(2)}
                    </Typography>
                  )}
                </>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleAddPayment}
            variant="contained"
            disabled={(() => {
              const enteredAmount = Number(paymentAmount);
              const totalAmount = Number(selectedBooking?.total_amount || 0);
              const paidAmount = Number(selectedBooking?.paid_amount || 0);
              const remainingAmount = totalAmount - paidAmount;
              const isOverpayment = !isNaN(enteredAmount) && enteredAmount > remainingAmount;
              
              return !paymentAmount || 
                     isNaN(enteredAmount) || 
                     enteredAmount <= 0 || 
                     isOverpayment || 
                     paymentLoading;
            })()}
            style={{ pointerEvents: paymentLoading ? 'none' : 'auto' }}
          >
            {paymentLoading ? 'Adding...' : 'Add Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={showEditPaymentDialog} onClose={handleCloseEditPaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoney />
          Edit Payment
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {editingPaymentHistory && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Payment Date: {format(parseISO(editingPaymentHistory.created_at), 'PPP')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Amount: {formatAmountWithCurrency(editingPaymentHistory, 'amount')}
                </Typography>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Edit Amount"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={editPaymentAmount}
                  onChange={(e) => setEditPaymentAmount(e.target.value)}
                  error={Number(editPaymentAmount) <= 0}
                  inputProps={{
                    min: 0,
                    step: 0.01
                  }}
                  sx={{ mt: 2 }}
                />
                <TextField
                  margin="dense"
                  label="Reason (optional)"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={editPaymentReason}
                  onChange={(e) => setEditPaymentReason(e.target.value)}
                  sx={{ mt: 1 }}
                />
                {Number(editPaymentAmount) <= 0 && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Amount must be greater than 0
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditPaymentDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleEditPayment}
            variant="contained"
            disabled={(() => {
              const enteredAmount = Number(editPaymentAmount);
              return !editPaymentAmount || 
                     isNaN(enteredAmount) || 
                     enteredAmount <= 0 || 
                     editPaymentLoading;
            })()}
            style={{ pointerEvents: editPaymentLoading ? 'none' : 'auto' }}
          >
            {editPaymentLoading ? 'Updating...' : 'Update Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check-in/out Dialog */}
      <Dialog open={showCheckinDialog} onClose={handleCloseCheckinDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Login />
          {t('checkin_checkout')} - {selectedBookingForCheckin?.tenant_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 2 }}>
                {t('select_checkin_status')}
              </FormLabel>
              <RadioGroup
                value={checkinStatus}
                onChange={(e) => setCheckinStatus(e.target.value as any)}
              >
                <FormControlLabel
                  value="not_checked_in"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Key sx={{ fontSize: 18, color: '#ffc107' }} />
                      <span>{t('waiting_for_checkin')}</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="checked_in"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Login sx={{ fontSize: 18, color: '#4caf50' }} />
                      <span>{t('checked_in')}</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="checked_out"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Logout sx={{ fontSize: 18, color: '#ff9800' }} />
                      <span>{t('checked_out')}</span>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
            
            {checkinStatus === 'checked_in' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CleaningServices sx={{ fontSize: 16, color: '#ff9800' }} />
                  {t('unit_cleaning_note')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCheckinDialog} disabled={checkinLoading}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSaveCheckinStatus} 
            variant="contained"
            disabled={checkinLoading}
            startIcon={checkinLoading ? undefined : <Login />}
          >
            {checkinLoading ? t('updating_status') : t('update_status')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cleaning Dialog */}
      <Dialog open={showCleaningDialog} onClose={handleCloseCleaningDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CleaningServices />
          {t('cleaning')} - {selectedUnitForCleaning?.unit_number}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 2 }}>
                {t('select_cleaning_status')}
              </FormLabel>
              <RadioGroup
                value={cleaningStatus}
                onChange={(e) => setCleaningStatus(e.target.value as any)}
              >
                <FormControlLabel
                  value="cleaned"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />
                      <span>{t('cleaned')}</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="not_cleaned"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CleaningServices sx={{ fontSize: 18, color: '#ff9800' }} />
                      <span>{t('not_cleaned')}</span>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCleaningDialog} disabled={cleaningLoading}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSaveCleaningStatus} 
            variant="contained"
            disabled={cleaningLoading}
            startIcon={cleaningLoading ? undefined : <CleaningServices />}
          >
            {cleaningLoading ? t('updating_status') : t('update_status')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expense Dialog */}
      <ExpenseDialog
        open={showExpenseDialog}
        onClose={() => {
          setShowExpenseDialog(false);
          setExpenseProperty(null);
          setExpenseUnit(null);
        }}
        property={expenseProperty || undefined}
        unit={expenseUnit || undefined}
      />

      {/* Purchase Invoice Dialog */}
      <PurchaseInvoiceDialog
        open={showPurchaseInvoiceDialog}
        onClose={() => {
          setShowPurchaseInvoiceDialog(false);
          setPurchaseInvoiceProperty(null);
          setPurchaseInvoiceUnit(null);
        }}
        property={purchaseInvoiceProperty || undefined}
        unit={purchaseInvoiceUnit || undefined}
      />

      {/* Ticket Dialog */}
      <Dialog 
        open={showTicketDialog} 
        onClose={() => setShowTicketDialog(false)} 
        maxWidth="sm" 
        fullWidth
        dir={ticketLanguage === 'ar' ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            '@media print': {
              boxShadow: 'none',
              margin: 0,
              padding: 0,
              maxWidth: 'none',
              width: '100%',
              height: 'auto',
              overflow: 'visible',
              transform: 'scale(0.9)',
              transformOrigin: 'top left',
              pageBreakInside: 'avoid',
              pageBreakAfter: 'avoid'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold', 
          fontSize: '1.5rem',
          '@media print': {
            fontSize: '1.2rem',
            padding: '8px 0'
          }
        }}>
          {ticketLanguage === 'ar' ? 'تذكرة الحجز' : 'Booking Ticket'}
        </DialogTitle>
        <DialogContent sx={{
          '@media print': {
            padding: '0 16px',
            margin: 0,
            fontSize: '0.9rem'
          }
        }}>
          {selectedBooking && (
            <Box sx={{ 
              p: 2, 
              fontFamily: 'Arial, sans-serif', 
              lineHeight: 2,
              '@media print': {
                padding: '8px',
                lineHeight: 1.4,
                fontSize: '0.85rem'
              }
            }}>
              {ticketLanguage === 'ar' ? (
                <>
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    fontWeight: 'bold', 
                    mb: 1,
                    '@media print': {
                      fontSize: '0.9rem',
                      mb: 0.5
                    }
                  }}>
                    <strong>رقم الحجز :</strong> {selectedBooking.booking_number || selectedBooking.id}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>الاسم :</strong> {selectedBooking.tenant_name}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>رقم الموبايل :</strong> {selectedBooking.tenant_phone || 'غير محدد'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>العقار المؤجر :</strong> {selectedBooking.property?.name} - {getUnitHierarchyNameById(selectedBooking.unit_id)}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>تاريخ الدخول :</strong> {(() => {
                      const startDate = new Date(selectedBooking.start_date);
                      return startDate.toLocaleDateString('ar-EG', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                    })()}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>وقت الدخول :</strong> 03:00 عصراً
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>تاريخ المغادرة :</strong> {(() => {
                      const endDate = new Date(selectedBooking.end_date);
                      return endDate.toLocaleDateString('ar-EG', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                    })()}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>وقت المغادرة :</strong> 12:00 ظهراً
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>عدد الليالي :</strong> {(() => {
                      const startDate = new Date(selectedBooking.start_date);
                      const endDate = new Date(selectedBooking.end_date);
                      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return diffDays;
                    })()} ليلة (غير قابل للتمديد)
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>الحد الأقصى للأشخاص :</strong> {(() => {
                      const unit = units.find(u => u.id === selectedBooking.unit_id);
                      const maxPersons = unit?.max_persons;
                      if (!maxPersons) return 'غير محدد';
                      
                      if (maxPersons === 1) return 'شخص واحد';
                      if (maxPersons === 2) return 'شخصان';
                      if (maxPersons >= 3 && maxPersons <= 10) {
                        const arabicNumbers = ['٣', '٤', '٥', '٦', '٧', '٨', '٩', '١٠'];
                        return `${arabicNumbers[maxPersons - 3]} أشخاص`;
                      }
                      if (maxPersons > 10) {
                        const arabicNumbers = ['١١', '١٢', '١٣', '١٤', '١٥', '١٦', '١٧', '١٨', '١٩', '٢٠'];
                        const index = maxPersons - 11;
                        if (index < arabicNumbers.length) {
                          return `${arabicNumbers[index]} شخصاً`;
                        }
                        return `${maxPersons} شخصاً`;
                      }
                      return `${maxPersons} شخص`;
                    })()}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>ايجار الليلة :</strong> {(() => {
                      const startDate = new Date(selectedBooking.start_date);
                      const endDate = new Date(selectedBooking.end_date);
                      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      const perNight = diffDays > 0 ? (selectedBooking.rent_amount / diffDays).toFixed(2) : '0';
                      return perNight;
                    })()} {selectedBooking.currency?.symbol || 'ر.ع'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>اجمالي الايجار:</strong> {selectedBooking.rent_amount} {selectedBooking.currency?.symbol || 'ر.ع'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>المبلغ المدفوع :</strong> {selectedBooking.paid_amount || 0} {selectedBooking.currency?.symbol || 'ر.ع'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>الباقي :</strong> {(() => {
                      const totalRent = selectedBooking.rent_amount || 0;
                      const paidAmount = selectedBooking.paid_amount || 0;
                      const remaining = totalRent - paidAmount;
                      return remaining.toFixed(2);
                    })()} {selectedBooking.currency?.symbol || 'ر.ع'} تدفع عند استلام السكن.
                  </Typography>
                  
                  {/* Arabic Ticket Document Content */}
                  {ticketDocumentContent ? (
                    <Box sx={{ 
                      mt: 3, 
                      pt: 2, 
                      borderTop: '1px solid #ddd',
                      '@media print': {
                        mt: 1,
                        pt: 1,
                        pageBreakInside: 'avoid'
                      }
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '1rem', 
                          lineHeight: 1.8,
                          whiteSpace: 'pre-wrap',
                          textAlign: 'justify',
                          '@media print': {
                            fontSize: '0.75rem',
                            lineHeight: 1.4
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(ticketDocumentContent) }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      mt: 3, 
                      pt: 2, 
                      borderTop: '1px solid #ddd',
                      '@media print': {
                        mt: 1,
                        pt: 1
                      }
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.9rem', 
                          fontStyle: 'italic',
                          color: 'text.secondary',
                          '@media print': {
                            fontSize: '0.75rem'
                          }
                        }}
                      >
                        📄 لا توجد شروط وأحكام إضافية متاحة
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    fontWeight: 'bold', 
                    mb: 1,
                    '@media print': {
                      fontSize: '0.9rem',
                      mb: 0.5
                    }
                  }}>
                    <strong>Booking Number:</strong> {selectedBooking.booking_number || selectedBooking.id}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Name:</strong> {selectedBooking.tenant_name}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Mobile Number:</strong> {selectedBooking.tenant_phone || 'Not specified'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Rental Property:</strong> {selectedBooking.property?.name} - {getUnitHierarchyNameById(selectedBooking.unit_id)}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Check-in Date:</strong> {(() => {
                      const startDate = new Date(selectedBooking.start_date);
                      return startDate.toLocaleDateString('en-US', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                    })()}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Check-in Time:</strong> 03:00 PM
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Check-out Date:</strong> {(() => {
                      const endDate = new Date(selectedBooking.end_date);
                      return endDate.toLocaleDateString('en-US', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                    })()}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Check-out Time:</strong> 12:00 PM
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Number of Nights:</strong> {(() => {
                      const startDate = new Date(selectedBooking.start_date);
                      const endDate = new Date(selectedBooking.end_date);
                      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return diffDays;
                    })()} nights (non-extendable)
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Max Persons:</strong> {(() => {
                      const unit = units.find(u => u.id === selectedBooking.unit_id);
                      return unit?.max_persons || 'Not specified';
                    })()} persons
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Rent per Night:</strong> {(() => {
                      const startDate = new Date(selectedBooking.start_date);
                      const endDate = new Date(selectedBooking.end_date);
                      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      const perNight = diffDays > 0 ? (selectedBooking.rent_amount / diffDays).toFixed(2) : '0';
                      return perNight;
                    })()} {selectedBooking.currency?.symbol || 'OMR'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Total Rent:</strong> {selectedBooking.rent_amount} {selectedBooking.currency?.symbol || 'OMR'}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Paid Amount:</strong> {selectedBooking.paid_amount || 0} {selectedBooking.currency?.symbol || 'OMR'}
                  </Typography>
                   <Typography variant="body1" sx={{ 
                    mb: 1, 
                    fontSize: '1.1rem',
                    '@media print': {
                      fontSize: '0.85rem',
                      mb: 0.3
                    }
                  }}>
                    <strong>Remaining:</strong> {(() => {
                      const totalRent = selectedBooking.rent_amount || 0;
                      const paidAmount = selectedBooking.paid_amount || 0;
                      const remaining = totalRent - paidAmount;
                      return remaining.toFixed(2);
                    })()} {selectedBooking.currency?.symbol || 'OMR'} to be paid upon accommodation receipt.
                  </Typography>
                  
                  {/* English Ticket Document Content */}
                  {ticketDocumentContent ? (
                    <Box sx={{ 
                      mt: 3, 
                      pt: 2, 
                      borderTop: '1px solid #ddd',
                      '@media print': {
                        mt: 1,
                        pt: 1,
                        pageBreakInside: 'avoid'
                      }
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '1rem', 
                          lineHeight: 1.8,
                          whiteSpace: 'pre-wrap',
                          textAlign: 'justify',
                          '@media print': {
                            fontSize: '0.75rem',
                            lineHeight: 1.4
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(ticketDocumentContent) }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      mt: 3, 
                      pt: 2, 
                      borderTop: '1px solid #ddd',
                      '@media print': {
                        mt: 1,
                        pt: 1
                      }
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.9rem', 
                          fontStyle: 'italic',
                          color: 'text.secondary',
                          '@media print': {
                            fontSize: '0.75rem'
                          }
                        }}
                      >
                        📄 No additional terms and conditions available
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          px: 3, 
          py: 2,
          '@media print': {
            display: 'none'
          }
        }}>
          {/* Left side: Share, WhatsApp and Language buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Share />}
              onClick={handleShareTicket}
              sx={{ minWidth: 80 }}
            >
              {ticketLanguage === 'ar' ? 'مشاركة' : 'Share'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Phone />}
              onClick={() => {
                if (selectedBooking?.tenant_phone) {
                  const phoneNumber = selectedBooking.tenant_phone.replace(/\D/g, ''); // Remove non-digits
                  
                  // Calculate booking details
                  const startDate = new Date(selectedBooking.start_date);
                  const endDate = new Date(selectedBooking.end_date);
                  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  const perNight = diffDays > 0 ? (selectedBooking.rent_amount / diffDays).toFixed(2) : '0';
                  const totalRent = selectedBooking.rent_amount || 0;
                  const paidAmount = selectedBooking.paid_amount || 0;
                  const remaining = (totalRent - paidAmount).toFixed(2);
                  const currency = selectedBooking.currency?.symbol || (ticketLanguage === 'ar' ? 'ر.ع' : 'OMR');
                  
                  // Create comprehensive message with all booking details
                  const message = ticketLanguage === 'ar' 
                    ? `*تذكرة حجز - ${selectedBooking.tenant_name}*

🏠 *تفاصيل الحجز*
رقم الحجز: ${selectedBooking.booking_number || selectedBooking.id}
الاسم: ${selectedBooking.tenant_name}
رقم الموبايل: ${selectedBooking.tenant_phone || 'غير محدد'}
العقار المؤجر: ${selectedBooking.property?.name} - ${getUnitHierarchyNameById(selectedBooking.unit_id)}

📅 *تواريخ الإقامة*
تاريخ الدخول: ${startDate.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' })}
وقت الدخول: 03:00 عصراً
تاريخ المغادرة: ${endDate.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' })}
وقت المغادرة: 12:00 ظهراً
عدد الليالي: ${diffDays} ليلة (غير قابل للتمديد)
الحد الأقصى للأشخاص: ${(() => {
  const unit = units.find(u => u.id === selectedBooking.unit_id);
  const maxPersons = unit?.max_persons;
  if (!maxPersons) return 'غير محدد';
  
  if (maxPersons === 1) return 'شخص واحد';
  if (maxPersons === 2) return 'شخصان';
  if (maxPersons >= 3 && maxPersons <= 10) {
    const arabicNumbers = ['٣', '٤', '٥', '٦', '٧', '٨', '٩', '١٠'];
    return `${arabicNumbers[maxPersons - 3]} أشخاص`;
  }
  if (maxPersons > 10) {
    const arabicNumbers = ['١١', '١٢', '١٣', '١٤', '١٥', '١٦', '١٧', '١٨', '١٩', '٢٠'];
    const index = maxPersons - 11;
    if (index < arabicNumbers.length) {
      return `${arabicNumbers[index]} شخصاً`;
    }
    return `${maxPersons} شخصاً`;
  }
  return `${maxPersons} شخص`;
})()}

💰 *تفاصيل الدفع*
ايجار الليلة: ${perNight} ${currency}
اجمالي الايجار: ${totalRent} ${currency}
المبلغ المدفوع: ${paidAmount} ${currency}
الباقي: ${remaining} ${currency} تدفع عند استلام السكن

${ticketDocumentContent ? `\n📋 *الشروط والأحكام*\n${ticketDocumentContent.replace(/<[^>]*>/g, '')}` : ''}

شكراً لاختيارك خدماتنا! 🙏`
                    : `*Booking Ticket - ${selectedBooking.tenant_name}*

🏠 *Booking Details*
Booking Number: ${selectedBooking.booking_number || selectedBooking.id}
Name: ${selectedBooking.tenant_name}
Mobile Number: ${selectedBooking.tenant_phone || 'Not specified'}
Rental Property: ${selectedBooking.property?.name} - ${getUnitHierarchyNameById(selectedBooking.unit_id)}

📅 *Stay Dates*
Check-in Date: ${startDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
Check-in Time: 03:00 PM
Check-out Date: ${endDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
Check-out Time: 12:00 PM
Number of Nights: ${diffDays} nights (non-extendable)
Max Persons: ${(() => {
  const unit = units.find(u => u.id === selectedBooking.unit_id);
  return unit?.max_persons || 'Not specified';
})()} persons

💰 *Payment Details*
Rent per Night: ${perNight} ${currency}
Total Rent: ${totalRent} ${currency}
Paid Amount: ${paidAmount} ${currency}
Remaining: ${remaining} ${currency} to be paid upon accommodation receipt

${ticketDocumentContent ? `\n📋 *Terms and Conditions*\n${ticketDocumentContent.replace(/<[^>]*>/g, '')}` : ''}

Thank you for choosing our services! 🙏`;

                  window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
                } else {
                  alert(ticketLanguage === 'ar' ? 'رقم الهاتف غير متوفر' : 'Phone number not available');
                }
              }}
              sx={{ 
                minWidth: 80,
                color: '#25D366',
                borderColor: '#25D366',
                '&:hover': {
                  borderColor: '#25D366',
                  backgroundColor: 'rgba(37, 211, 102, 0.04)'
                }
              }}
            >
              {ticketLanguage === 'ar' ? 'واتساب' : 'WhatsApp'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Language />}
              onClick={toggleTicketLanguage}
              sx={{ minWidth: 80 }}
            >
              {ticketLanguage === 'ar' ? 'EN' : 'عربي'}
            </Button>
          </Box>

          {/* Right side: Print and Close buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              onClick={() => window.print()}
              sx={{ minWidth: 100 }}
            >
              {ticketLanguage === 'ar' ? 'طباعة' : 'Print'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => setShowTicketDialog(false)}
              sx={{ minWidth: 100 }}
            >
              {ticketLanguage === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Drag Feedback Snackbar */}
      <Snackbar
        open={!!dragFeedback}
        autoHideDuration={dragFeedback?.type === 'info' ? 8000 : 4000} // Longer duration for currency change alerts
        onClose={() => setDragFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setDragFeedback(null)} 
          severity={dragFeedback?.type || 'info'}
          sx={{ width: '100%' }}
        >
          {dragFeedback?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingCalendar;
